/* GHALIA — global UI behaviours.
   Vanilla JS. Custom elements for drawers, dialog, qty, header scroll, accordion, marquee duplication.
   No external libraries. */

(() => {
  if (!window.__GhaliaNet) {
    window.__GhaliaNet = {
      fetch: window.fetch?.bind(window),
      XHR: window.XMLHttpRequest,
    };
  }
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  };

  /* ---------- Sticky header scroll state + pinned nav ---------- */
  const header = document.querySelector('[data-site-header]');
  const primaryBar = document.querySelector('[data-announcement-primary]');
  const utilityBar = document.querySelector('[data-announcement-utility]');

  const getPrimaryScrollOffset = () => {
    if (!primaryBar || primaryBar.classList.contains('is-hidden')) return 0;
    return Math.max(0, primaryBar.offsetHeight - window.scrollY);
  };

  const isHeaderStackPinned = () => window.scrollY > 4;

  const setSpacer = (el, active, height) => {
    if (!el) return;
    el.classList.toggle('is-active', active);
    el.style.height = active ? `${height}px` : '0';
  };

  const pinUtilityBar = () => {
    const utilitySpacer = document.querySelector('[data-ann-utility-spacer]');
    const headerSpacer = document.querySelector('[data-header-nav-spacer]');
    const utilityH = utilityBar && !utilityBar.classList.contains('is-hidden') ? utilityBar.offsetHeight : 0;
    const headerH = header?.offsetHeight || 0;
    const pinned = isHeaderStackPinned();

    primaryBar?.classList.toggle('is-scrolled-away', pinned && !primaryBar.classList.contains('is-hidden'));

    if (utilityBar) {
      utilityBar.classList.toggle('is-pinned', pinned);
      setSpacer(utilitySpacer, pinned, utilityH);
    }

    if (header) {
      header.classList.toggle('is-nav-pinned', pinned);
      setSpacer(headerSpacer, pinned, headerH);
    }
  };

  const pinHeaderOnly = () => {
    const headerSpacer = document.querySelector('[data-header-nav-spacer]');
    const headerH = header?.offsetHeight || 0;
    const pinned = isHeaderStackPinned();

    primaryBar?.classList.toggle('is-scrolled-away', pinned && !primaryBar?.classList.contains('is-hidden'));

    if (header) {
      header.classList.toggle('is-nav-pinned', pinned);
      setSpacer(headerSpacer, pinned, headerH);
    }
    document.documentElement.style.setProperty('--sticky-nav-top', '0px');
  };

  const isMobileNavViewport = () => window.matchMedia('(max-width: 1024px)').matches;

  const updateMobileNavInset = () => {
    if (!isMobileNavViewport()) return;
    const group = document.querySelector('.shopify-section-group-header-group');
    const navHeader = document.querySelector('[data-site-header]');
    if (!group && !navHeader) return;
    const groupBottom = group?.getBoundingClientRect().bottom ?? 0;
    const headerBottom = navHeader?.getBoundingClientRect().bottom ?? 0;
    const inset = Math.max(0, Math.round(Math.max(groupBottom, headerBottom)));
    document.documentElement.style.setProperty('--mobile-nav-top', `${inset}px`);

    syncMobileNavPad();
  };

  const syncMobileNavPad = () => {
    if (!isMobileNavViewport()) return;
    const header = document.querySelector('[data-site-header]');
    const mark =
      header?.querySelector('.site-header__logo-img') ||
      header?.querySelector('.site-header__logo');
    const toggle = header?.querySelector('[data-mobile-nav-toggle]');
    const drawer = document.querySelector('[data-mobile-nav]');
    if (!mark) return;

    const markRect = mark.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl';
    const drawerRect = drawer?.getBoundingClientRect();
    const drawerWidth = drawerRect?.width || window.innerWidth * 0.85;
    const maxAlign = Math.max(16, Math.min(96, Math.round(drawerWidth * 0.42)));

    let align = rtl
      ? Math.max(0, Math.round(window.innerWidth - markRect.right))
      : Math.max(0, Math.round(markRect.left));

    if (drawer?.dataset.open === 'true' && drawerRect && drawerRect.width > 0) {
      const drawerLeft = drawerRect.left;
      const drawerRelative =
        rtl
          ? Math.max(0, Math.round(drawerRect.right - markRect.right))
          : Math.max(0, Math.round(markRect.left - drawerLeft));
      if (drawerLeft >= -4 && drawerLeft < window.innerWidth * 0.25) {
        align = drawerRelative;
      }
    }

    align = Math.min(align, maxAlign);
    document.documentElement.style.setProperty('--mobile-nav-align', `${align}px`);

    const toggleRect = toggle?.getBoundingClientRect();
    const headerPad = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-pad-inline') || '12',
      10
    );
    const footPadMax = Math.max(headerPad + 8, 56);
    let backPad = rtl
      ? Math.max(0, Math.round(window.innerWidth - (toggleRect?.right ?? markRect.left)))
      : Math.max(0, Math.round(toggleRect?.left ?? markRect.left));

    if (drawer?.dataset.open === 'true' && drawerRect && drawerRect.width > 0 && toggleRect) {
      const drawerLeft = drawerRect.left;
      if (drawerLeft >= -4 && drawerLeft < window.innerWidth * 0.25) {
        backPad = rtl
          ? Math.max(0, Math.round(drawerRect.right - toggleRect.right))
          : Math.max(0, Math.round(toggleRect.left - drawerLeft));
      }
    }

    backPad = Math.min(backPad, footPadMax);
    document.documentElement.style.setProperty('--mobile-nav-back-pad', `${backPad}px`);

    /* Footer (Farah + account) — align with header X (toggle inset + small nudge) */
    const footPad = Math.min(Math.max(backPad + 8, headerPad + 8), footPadMax);
    document.documentElement.style.setProperty('--mobile-nav-foot-pad', `${footPad}px`);

    const panelTitle = drawer?.querySelector('[data-mobile-nav-panel-title]');
    const panelBar = drawer?.querySelector('[data-mobile-nav-bar="panel"]');
    if (
      drawer?.dataset.open === 'true' &&
      panelBar &&
      !panelBar.hidden &&
      panelTitle?.textContent?.trim() &&
      drawerRect &&
      drawerRect.width > 0
    ) {
      const titleRect = panelTitle.getBoundingClientRect();
      const titlePad = rtl
        ? Math.max(0, Math.round(drawerRect.right - titleRect.right))
        : Math.max(0, Math.round(titleRect.left - drawerRect.left));
      document.documentElement.style.setProperty('--mobile-nav-panel-title-pad', `${titlePad}px`);
    } else {
      document.documentElement.style.removeProperty('--mobile-nav-panel-title-pad');
    }
  };

  const updateStickyNav = () => {
    const utility = document.querySelector('[data-announcement-utility]');
    const primary = document.querySelector('[data-announcement-primary]');
    const utilityH = utility && !utility.classList.contains('is-hidden') ? utility.offsetHeight : 0;
    const pinned = isHeaderStackPinned();

    if (utilityBar) pinUtilityBar();
    else pinHeaderOnly();

    const primaryH = primary && !primary.classList.contains('is-hidden') && !primary.classList.contains('is-scrolled-away')
      ? primary.offsetHeight
      : 0;

    document.documentElement.style.setProperty('--ann-utility-top', '0px');
    document.documentElement.style.setProperty('--sticky-nav-top', pinned ? `${utilityH}px` : '0px');
    document.documentElement.style.setProperty('--announcement-h', `${primaryH + utilityH}px`);
    document.documentElement.style.setProperty('--ann-utility-h', `${utilityH}px`);

    if (header) {
      const overlay = header.classList.contains('site-header--overlay');
      let atTop = !pinned && window.scrollY <= 12;
      if (overlay && primaryBar && !primaryBar.classList.contains('is-hidden') && !primaryBar.classList.contains('is-scrolled-away')) {
        atTop = window.scrollY <= Math.max(12, primaryBar.offsetHeight - 4);
      }
      header.dataset.atTop = atTop ? 'true' : 'false';
      header.dataset.scrolled = atTop ? 'false' : 'true';
      document.documentElement.dataset.headerStuck = pinned ? 'true' : 'false';
    }

    updateMobileNavInset();
  };

  if (header || utilityBar || primaryBar) {
    document.addEventListener('scroll', updateStickyNav, { passive: true });
    updateStickyNav();
  }
  window.addEventListener('resize', updateMobileNavInset, { passive: true });
  updateMobileNavInset();

  document.addEventListener('ghalia:drawer:open', (e) => {
    if (!e.detail?.drawer?.hasAttribute('data-mobile-nav')) return;
    requestAnimationFrame(() => {
      syncMobileNavPad();
      requestAnimationFrame(syncMobileNavPad);
    });
  });

  /* ---------- Drawer / overlay coordination ---------- */
  const overlay = document.querySelector('[data-overlay]');

  const syncDrawerTriggers = () => {
    $$('[data-drawer-open], [data-drawer-toggle]').forEach((btn) => {
      const sel = btn.dataset.drawerOpen || btn.dataset.drawerToggle;
      if (!sel) return;
      const drawer = document.querySelector(sel);
      const open = drawer?.dataset.open === 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (btn.hasAttribute('data-mobile-nav-toggle')) {
        const label = open ? btn.dataset.labelClose : btn.dataset.labelMenu;
        if (label) btn.setAttribute('aria-label', label);
      }
    });
  };

  let resetMobileNavView = null;

  const CART_DISMISS_DURING_ADD = 'data-user-dismissed-during-add';

  const closeDrawers = () => {
    const hadMobileNav = document.querySelector('[data-mobile-nav][data-open="true"]');
    if (cartDrawer?.dataset.open === 'true') {
      cartDrawer.setAttribute(CART_DISMISS_DURING_ADD, 'true');
    }
    window.GhaliaCartQuickAdd?.close?.();
    cartDrawer?.classList.remove('is-quick-add-open');
    $$('.drawer[data-open="true"]').forEach((d) => {
      d.dataset.open = 'false';
      d.setAttribute('aria-hidden', 'true');
    });
    if (overlay) overlay.dataset.open = 'false';
    document.documentElement.classList.remove('drawer-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    syncDrawerTriggers();
    if (hadMobileNav) resetMobileNavView?.();
  };

  const isMobileNavDrawer = (el) => el?.hasAttribute('data-mobile-nav');

  const shouldSkipDrawerOverlay = (drawer) => {
    if (!isMobileNavViewport()) return false;
    if (isMobileNavDrawer(drawer)) return true;
    if (drawer?.hasAttribute('data-size-drawer')) return true;
    return false;
  };

  const openDrawer = (drawer, options = {}) => {
    if (!drawer) return;
    $$('.drawer[data-open="true"]').forEach((d) => {
      if (d !== drawer) {
        d.dataset.open = 'false';
        d.setAttribute('aria-hidden', 'true');
      }
    });
    drawer.dataset.open = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    const skipOverlay = shouldSkipDrawerOverlay(drawer);
    if (overlay) overlay.dataset.open = skipOverlay ? 'false' : 'true';
    document.documentElement.classList.add('drawer-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    syncDrawerTriggers();
    updateMobileNavInset();
    document.dispatchEvent(
      new CustomEvent('ghalia:drawer:open', {
        detail: { drawer, preserveQuickAdd: Boolean(options.preserveQuickAdd) },
      })
    );
    if (isMobileNavDrawer(drawer) && isMobileNavViewport()) {
      requestAnimationFrame(() => {
        syncMobileNavPad();
        requestAnimationFrame(syncMobileNavPad);
      });
      const toggle = document.querySelector('[data-mobile-nav-toggle]');
      toggle?.focus({ preventScroll: true });
    } else {
      const focusable = drawer.querySelector(
        '[data-drawer-close], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus({ preventScroll: true });
    }
  };

  const syncOverlayWithOpenDrawer = () => {
    const openDrawerEl = document.querySelector('.drawer[data-open="true"]');
    if (!openDrawerEl) {
      if (overlay?.dataset.open === 'true') overlay.dataset.open = 'false';
      return;
    }
    if (overlay) overlay.dataset.open = shouldSkipDrawerOverlay(openDrawerEl) ? 'false' : 'true';
  };

  syncOverlayWithOpenDrawer();
  window.addEventListener('pageshow', syncOverlayWithOpenDrawer);

  const wishlistDrawerEl = () => document.querySelector('[data-wishlist-drawer]');
  const openWishlistDrawer = () => {
    if (typeof window.GhaliaWishlistOpen === 'function') {
      window.GhaliaWishlistOpen();
      return;
    }
    const drawer = wishlistDrawerEl();
    if (drawer) openDrawer(drawer);
  };

  window.GhaliaUI = window.GhaliaUI || {};
  window.GhaliaUI.openWishlistDrawer = openWishlistDrawer;

  const isWishlistUrl = (href) => {
    if (!href) return false;
    try {
      const path = href.startsWith('http') ? new URL(href, window.location.origin).pathname : href;
      return /\/pages\/(wishlist|swym-wishlist|swym)(\/|$)/i.test(path);
    } catch (_) {
      return /\/pages\/(wishlist|swym)/i.test(href);
    }
  };

  const ensureWishlistHeaderOpensDrawer = () => {
    let btn = document.querySelector('[data-wishlist-header-btn]');
    if (!btn) {
      const swymBtn = document.querySelector(
        '.site-actions__btn--wishlist a, .site-actions__btn--wishlist.swym-wishlist, a.swym-wishlist'
      );
      if (swymBtn) btn = swymBtn;
    }
    if (!btn || btn.matches('[data-drawer-open]')) return;

    if (btn.tagName === 'A') {
      const replacement = document.createElement('button');
      replacement.type = 'button';
      replacement.className = btn.className;
      replacement.innerHTML = btn.innerHTML;
      replacement.setAttribute('data-wishlist-header-btn', '');
      replacement.setAttribute('data-drawer-open', '[data-wishlist-drawer]');
      replacement.setAttribute('aria-label', btn.getAttribute('aria-label') || 'Open wishlist');
      btn.replaceWith(replacement);
      return;
    }

    btn.setAttribute('data-drawer-open', '[data-wishlist-drawer]');
    if (btn.tagName === 'A') btn.removeAttribute('href');
  };

  document.addEventListener(
    'click',
    (e) => {
      const link = e.target.closest(
        'a[href*="/pages/wishlist"], a[href*="/pages/swym-wishlist"], a[href*="/pages/swym"], a.swym-wishlist, .swym-wishlist'
      );
      if (!link || link.hasAttribute('data-drawer-close')) return;
      const href = link.getAttribute('href') || '';
      if (!isWishlistUrl(href) && !link.classList.contains('swym-wishlist')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openWishlistDrawer();
    },
    true
  );

  const handleDrawerClick = (e) => {
    const toggle = e.target.closest('[data-drawer-toggle]');
    if (toggle) {
      e.preventDefault();
      const drawer = document.querySelector(toggle.dataset.drawerToggle);
      if (drawer?.dataset.open === 'true') {
        closeDrawers();
      } else {
        openDrawer(drawer);
      }
      return;
    }

    const closerOnly = e.target.closest('[data-drawer-close]:not([data-drawer-open])');
    if (closerOnly) {
      const href = closerOnly.getAttribute('href') || '';
      const shouldNavigate =
        closerOnly.tagName === 'A' && href && href !== '#' && !href.toLowerCase().startsWith('javascript:');
      if (!shouldNavigate) e.preventDefault();
      closeDrawers();
      return;
    }

    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      if (opener.dataset.drawerOpen === '[data-wishlist-drawer]') {
        openWishlistDrawer();
        return;
      }
      const drawer = document.querySelector(opener.dataset.drawerOpen);
      if (!drawer) return;
      if (drawer.dataset.open === 'true') {
        closeDrawers();
        return;
      }
      if (drawer.hasAttribute('data-size-drawer') && opener.dataset.sizeSegment) {
        drawer.dataset.activeSegment = opener.dataset.sizeSegment;
      }
      if (drawer.hasAttribute('data-care-drawer') && opener.dataset.careSegment) {
        drawer.dataset.activeSegment = opener.dataset.careSegment;
      }
      if (drawer.hasAttribute('data-usp-policy-drawer') && opener.dataset.uspPanel) {
        const panelId = opener.dataset.uspPanel;
        drawer.querySelectorAll('[data-usp-panel]').forEach((panel) => {
          const on = panel.dataset.uspPanel === panelId;
          panel.hidden = !on;
        });
        const titleEl = drawer.querySelector('[data-usp-drawer-title]');
        const activePanel = drawer.querySelector(`[data-usp-panel="${panelId}"]`);
        if (titleEl && activePanel) {
          const titleText = activePanel.dataset.uspDrawerTitleText;
          const heading = activePanel.querySelector('.usp-policy-drawer__heading');
          if (titleText) titleEl.textContent = decodeHtmlEntities(titleText);
          else if (heading) titleEl.textContent = heading.textContent.trim();
        }
        if (panelId === 'shipping' && activePanel) {
          const shippingRoot = activePanel.querySelector('[data-usp-shipping-regions]');
          if (shippingRoot) {
            shippingRoot.querySelectorAll('[data-shipping-region]').forEach((btn) => {
              const on = btn.dataset.shippingRegion === 'ksa';
              btn.classList.toggle('is-active', on);
              btn.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            shippingRoot.querySelectorAll('[data-shipping-region-pane]').forEach((pane) => {
              pane.hidden = pane.dataset.shippingRegionPane !== 'ksa';
            });
          }
        }
      }
      closeDrawers();
      openDrawer(drawer);
      return;
    }

    if (e.target.closest('[data-overlay]')) {
      closeDrawers();
      return;
    }

    /* Mobile menu: no dim overlay — close when tapping the visible page strip or header */
    const mobileNavOpen = document.querySelector('[data-mobile-nav][data-open="true"]');
    if (mobileNavOpen && isMobileNavViewport()) {
      if (
        !e.target.closest('[data-mobile-nav]') &&
        !e.target.closest('[data-mobile-nav-toggle]')
      ) {
        closeDrawers();
      }
    }
  };

  /* ---------- Size guide drawer + page ---------- */
  const initSizeGuide = (root) => {
    if (!root) return;

    const types = ['rings', 'bracelets', 'necklaces', 'earrings'];
    let activeType = 'rings';
    let activeTab = 'guide';

    const hash = window.location.hash.replace('#', '');
    if (root.hasAttribute('data-size-guide-page') && types.includes(hash)) {
      activeType = hash;
    }

    const updateSizeGuide = () => {
      root.querySelectorAll('[data-size-type]').forEach((chip) => {
        const on = chip.dataset.sizeType === activeType;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-size-tab]').forEach((tab) => {
        const on = tab.dataset.sizeTab === activeTab;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const chips = root.querySelector('.size-drawer__chips, .size-guide-page__chips');
      if (chips) chips.hidden = activeTab !== 'guide';
      const isFullPage = root.hasAttribute('data-size-guide-page');
      root.querySelectorAll('[data-size-pane]').forEach((pane) => {
        if (isFullPage && activeTab === 'guide') {
          pane.hidden = pane.dataset.sizeMode !== 'guide';
          return;
        }
        pane.hidden = !(pane.dataset.sizePane === activeType && pane.dataset.sizeMode === activeTab);
      });
    };

    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-size-type]');
      if (chip && root.contains(chip)) {
        activeType = chip.dataset.sizeType;
        if (root.hasAttribute('data-size-guide-page')) {
          const nextHash = `#${activeType}`;
          if (window.location.hash !== nextHash) {
            history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
          }
          if (activeTab === 'guide') {
            const target = root.querySelector(`[data-size-pane="${activeType}"][data-size-mode="guide"]`);
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        updateSizeGuide();
        return;
      }
      const tab = e.target.closest('[data-size-tab]');
      if (tab && root.contains(tab)) {
        activeTab = tab.dataset.sizeTab;
        updateSizeGuide();
      }
    });

    if (root.hasAttribute('data-size-drawer')) {
      document.addEventListener('ghalia:drawer:open', (e) => {
        if (e.detail?.drawer !== root) return;
        const segment = root.dataset.activeSegment;
        if (segment && root.querySelector(`[data-size-type="${segment}"]`)) {
          activeType = segment;
          activeTab = 'guide';
        }
        updateSizeGuide();
      });
    }

    updateSizeGuide();

    if (root.hasAttribute('data-size-guide-page') && types.includes(hash) && activeTab === 'guide') {
      const target = root.querySelector(`[data-size-pane="${activeType}"][data-size-mode="guide"]`);
      if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  initSizeGuide(document.querySelector('[data-size-drawer]'));
  initSizeGuide(document.querySelector('[data-size-guide-page]'));

  /* ---------- Jewelry care drawer + page ---------- */
  const initCareGuide = (root) => {
    if (!root) return;

    const materialTypes = ['vermeil', 'sterling', 'pearls'];
    let activeTab = 'essentials';
    let activeMaterial = 'vermeil';

    const hash = window.location.hash.replace('#', '');
    if (root.hasAttribute('data-care-guide-page')) {
      if (['essentials', 'materials', 'avoid'].includes(hash)) {
        activeTab = hash;
      }
      if (materialTypes.includes(hash)) {
        activeTab = 'materials';
        activeMaterial = hash;
      }
    }

    const updateCareGuide = () => {
      root.querySelectorAll('[data-care-tab]').forEach((tab) => {
        const on = tab.dataset.careTab === activeTab;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const chips = root.querySelector('.care-drawer__chips, .care-guide-page__chips');
      if (chips) chips.hidden = activeTab !== 'materials';
      root.querySelectorAll('[data-care-type]').forEach((chip) => {
        const on = chip.dataset.careType === activeMaterial;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-care-pane]').forEach((pane) => {
        const paneId = pane.dataset.carePane;
        let show = false;
        if (activeTab === 'essentials' && paneId === 'essentials') show = true;
        if (activeTab === 'avoid' && paneId === 'avoid') show = true;
        if (activeTab === 'materials' && paneId === activeMaterial) show = true;
        pane.hidden = !show;
      });
    };

    root.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-care-tab]');
      if (tab && root.contains(tab)) {
        activeTab = tab.dataset.careTab;
        if (root.hasAttribute('data-care-guide-page')) {
          const nextHash = `#${activeTab}`;
          if (window.location.hash !== nextHash) {
            history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
          }
        }
        updateCareGuide();
        return;
      }
      const chip = e.target.closest('[data-care-type]');
      if (chip && root.contains(chip)) {
        activeMaterial = chip.dataset.careType;
        activeTab = 'materials';
        if (root.hasAttribute('data-care-guide-page')) {
          const nextHash = `#${activeMaterial}`;
          if (window.location.hash !== nextHash) {
            history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
          }
        }
        updateCareGuide();
      }
    });

    if (root.hasAttribute('data-care-drawer')) {
      document.addEventListener('ghalia:drawer:open', (e) => {
        if (e.detail?.drawer !== root) return;
        const segment = root.dataset.activeSegment;
        if (segment === 'avoid') {
          activeTab = 'avoid';
        } else if (segment === 'materials' || materialTypes.includes(segment)) {
          activeTab = 'materials';
          if (materialTypes.includes(segment)) activeMaterial = segment;
        } else {
          activeTab = 'essentials';
        }
        updateCareGuide();
      });
    }

    updateCareGuide();
  };

  initCareGuide(document.querySelector('[data-care-drawer]'));
  initCareGuide(document.querySelector('[data-care-guide-page]'));

  /* ---------- USP policy — shipping region pills (drawer + policy pages) ---------- */
  document.querySelectorAll('[data-usp-shipping-regions]').forEach((root) => {
    root.addEventListener('click', (e) => {
      const regionBtn = e.target.closest('[data-shipping-region]');
      if (!regionBtn || !root.contains(regionBtn)) return;
      const regionId = regionBtn.dataset.shippingRegion;
      root.querySelectorAll('[data-shipping-region]').forEach((btn) => {
        const on = btn.dataset.shippingRegion === regionId;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-shipping-region-pane]').forEach((pane) => {
        pane.hidden = pane.dataset.shippingRegionPane !== regionId;
      });
    });
  });

  document.addEventListener('click', handleDrawerClick);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawers(); });

  document.addEventListener('click', (e) => {
    if (e.target.closest('.mobile-nav a[href]')) closeDrawers();
  });

  /* ---------- Mobile nav sub-panels ---------- */
  const mobileNavEl = document.querySelector('[data-mobile-nav]');
  if (mobileNavEl) {
    const mobileNavRootBar = mobileNavEl.querySelector('[data-mobile-nav-bar="root"]');
    const mobileNavPanelBar = mobileNavEl.querySelector('[data-mobile-nav-bar="panel"]');
    const mobileNavPanelTitle = mobileNavEl.querySelector('[data-mobile-nav-panel-title]');
    const mobileNavViews = () => Array.from(mobileNavEl.querySelectorAll('[data-mobile-nav-view]'));

    const setMobileNavView = (viewId) => {
      const id = viewId || 'root';
      mobileNavEl.dataset.mobileNavActiveView = id;
      mobileNavViews().forEach((view) => {
        const active = view.dataset.mobileNavView === id;
        view.hidden = !active;
        view.classList.toggle('is-active', active);
      });
      const isRoot = id === 'root';
      if (mobileNavRootBar) mobileNavRootBar.hidden = !isRoot;
      if (mobileNavPanelBar) mobileNavPanelBar.hidden = isRoot;
      mobileNavEl.querySelectorAll('[data-mobile-nav-open]').forEach((btn) => {
        btn.setAttribute('aria-expanded', btn.getAttribute('data-mobile-nav-open') === id ? 'true' : 'false');
      });
      if (!isRoot) {
        requestAnimationFrame(() => {
          syncMobileNavPad();
          requestAnimationFrame(syncMobileNavPad);
        });
      }
    };

    resetMobileNavView = () => setMobileNavView('root');
    setMobileNavView('root');

    mobileNavEl.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-mobile-nav-open]');
      if (openBtn) {
        e.preventDefault();
        const panelId = openBtn.getAttribute('data-mobile-nav-open');
        const title = openBtn.getAttribute('data-mobile-nav-title') || openBtn.textContent?.trim() || '';
        if (mobileNavPanelTitle) mobileNavPanelTitle.textContent = title;
        if (mobileNavEl.querySelector(`[data-mobile-nav-view="${panelId}"]`)) {
          setMobileNavView(panelId);
        }
        return;
      }
      if (e.target.closest('[data-mobile-nav-back]')) {
        e.preventDefault();
        setMobileNavView('root');
      }
    });

    document.addEventListener('ghalia:drawer:open', (e) => {
      if (e.detail?.drawer?.hasAttribute('data-mobile-nav')) setMobileNavView('root');
    });
  }

  syncDrawerTriggers();

  /* ---------- Mega menu (hover / click — desktop) ---------- */
  const megaItems = header ? $$('.site-nav__item--has-mega', header) : [];
  const megaPanels = header ? $$('[data-mega-panel]', header) : [];
  const megaPanelsWrap = header?.querySelector('[data-mega-panels]');
  const isDesktopNav = () => window.matchMedia('(min-width: 1025px)').matches;

  const navIdFor = (el) => el?.getAttribute('data-nav-id') ?? '';
  const panelIdFor = (el) => el?.getAttribute('data-mega-panel') ?? '';
  const categoryIdFor = (el) => el?.getAttribute('data-category-id') ?? '';

  const panelForItem = (item) => {
    const navId = navIdFor(item);
    const catId = categoryIdFor(item);
    return megaPanels.find((panel) => {
      if (panelIdFor(panel) === navId) return true;
      if (catId && categoryIdFor(panel) === catId) return true;
      return false;
    });
  };

  const itemForPanel = (panel) => {
    const panelId = panelIdFor(panel);
    const catId = categoryIdFor(panel);
    return megaItems.find((navItem) => {
      if (navIdFor(navItem) === panelId) return true;
      if (catId && categoryIdFor(navItem) === catId) return true;
      return false;
    });
  };

  const syncMegaPanels = () => {
    megaPanels.forEach((panel) => {
      const item = itemForPanel(panel);
      panel.dataset.open = item?.dataset.open === 'true' ? 'true' : 'false';
    });
    if (megaPanelsWrap) {
      const open = megaItems.some((item) => item.dataset.open === 'true');
      megaPanelsWrap.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
  };

  const syncMegaHeader = () => {
    if (!header) return;
    const open = megaItems.some((item) => item.dataset.open === 'true');
    header.dataset.megaOpen = open ? 'true' : 'false';
    header.classList.toggle('site-header--mega-open', open);
    document.documentElement.classList.toggle('is-mega-open', open);
    syncMegaPanels();
  };

  const closeAllMegas = () => {
    megaItems.forEach((item) => {
      item.dataset.open = 'false';
    });
    syncMegaHeader();
  };

  if (megaItems.length && header) {
    let closeTimer;

    const openItem = (item) => {
      if (!isDesktopNav()) return;
      clearTimeout(closeTimer);
      megaItems.forEach((other) => {
        if (other !== item) other.dataset.open = 'false';
      });
      item.dataset.open = 'true';
      syncMegaHeader();
    };

    const siteNav = header.querySelector('.site-nav');
    const resolveMegaItem = (target) => {
      if (!(target instanceof Element)) return null;
      const item = target.closest('.site-nav__item--has-mega');
      return item && header.contains(item) ? item : null;
    };

    const bindMegaHover = (item) => {
      const link = item.querySelector('.site-nav__link');
      const openThisItem = () => openItem(item);
      item.addEventListener('mouseenter', openThisItem);
      link?.addEventListener('mouseenter', openThisItem);
      link?.addEventListener('focusin', openThisItem);
      panelForItem(item)?.addEventListener('mouseenter', openThisItem);
    };

    megaItems.forEach(bindMegaHover);

    siteNav?.addEventListener('focusin', (e) => {
      const item = resolveMegaItem(e.target);
      if (item) openItem(item);
    });

    megaPanelsWrap?.addEventListener('mouseover', (e) => {
      const panel = e.target.closest('[data-mega-panel]');
      if (!panel) return;
      const item = itemForPanel(panel);
      if (item) openItem(item);
    });

    header.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    header.addEventListener('mouseleave', (e) => {
      if (!isDesktopNav()) return;
      if (e.relatedTarget instanceof Node && header.contains(e.relatedTarget)) return;
      closeTimer = setTimeout(closeAllMegas, 180);
    });

    document.addEventListener('click', (e) => {
      if (!isDesktopNav()) return;
      if (e.target.closest('[data-site-header]')) return;
      if (megaItems.some((item) => item.dataset.open === 'true')) closeAllMegas();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllMegas();
    });

    syncMegaHeader();
  }

  /* ---------- Cart drawer ---------- */
  const cartDrawer = document.querySelector('[data-cart-drawer]');

  const showGhaliaToast = (message) => {
    if (!message) return;
    const now = Date.now();
    if (
      message === showGhaliaToast._lastMessage &&
      now - (showGhaliaToast._lastAt || 0) < 2000
    ) {
      return;
    }
    showGhaliaToast._lastMessage = message;
    showGhaliaToast._lastAt = now;
    let toast = document.querySelector('[data-ghalia-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ghalia-toast';
      toast.setAttribute('data-ghalia-toast', '');
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.dataset.visible = 'true';
    clearTimeout(showGhaliaToast._timer);
    showGhaliaToast._timer = window.setTimeout(() => {
      toast.dataset.visible = 'false';
    }, 2600);
  };

  let suppressDrawerRefreshHandler = false;
  const cartLineMutations = new Map();
  const variantStockCache = new Map();

  const getCartLineRoot = (key) =>
    cartDrawer?.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`) ||
    document.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`);

  const pruneRemovedCartLines = (cart) => {
    const drawer = cartDrawer || document.querySelector('[data-cart-drawer]');
    if (!drawer || !cart?.items) return;
    const keys = new Set(cart.items.map((item) => item.key));
    drawer.querySelectorAll('[data-cart-line-key]').forEach((line) => {
      if (!keys.has(line.dataset.cartLineKey)) line.remove();
    });
  };

  const setCartLineBusy = (key, busy) => {
    const line = getCartLineRoot(key);
    if (!line) return;
    line.classList.toggle('cart-line--busy', busy);
    line.querySelectorAll('[data-cart-line-remove]').forEach((el) => {
      el.disabled = busy;
    });
  };

  const cloneCart = (cart) => {
    if (!cart?.items) return { items: [], item_count: 0, total_price: 0 };
    return JSON.parse(JSON.stringify(cart));
  };

  const patchCartLineQuantity = (cart, key, quantity) => {
    const next = cloneCart(cart);
    const idx = next.items.findIndex((item) => item.key === key);
    if (idx === -1) return next;
    if (quantity <= 0) {
      next.items.splice(idx, 1);
    } else {
      const item = next.items[idx];
      const prevQty = Math.max(1, Number(item.quantity) || 1);
      const unitFinal =
        Number(item.final_price ?? item.price) ||
        Math.round((Number(item.final_line_price ?? item.line_price) || 0) / prevQty);
      const unitOriginal =
        Number(item.original_price ?? item.price) || unitFinal;
      const unitLine = Math.round((Number(item.line_price) || unitOriginal * prevQty) / prevQty);
      item.quantity = quantity;
      item.final_line_price = unitFinal * quantity;
      item.original_line_price = unitOriginal * quantity;
      item.line_price = unitLine * quantity;
      if (item.total_discount != null) {
        item.total_discount = Math.round((Number(item.total_discount) / prevQty) * quantity);
      }
      if (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length) {
        item.line_level_discount_allocations = item.line_level_discount_allocations.map((alloc) => ({
          ...alloc,
          amount: Math.round((Number(alloc.amount) || 0) / prevQty * quantity),
        }));
      }
    }
    next.item_count = next.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    let subtotal = 0;
    let discount = 0;
    next.items.forEach((item) => {
      subtotal += Number(item.final_line_price ?? item.line_price) || 0;
      discount += Number(item.total_discount) || 0;
    });
    next.items_subtotal_price = subtotal;
    next.total_price = subtotal;
    next.total_discount = discount;
    return next;
  };

  let cartPricingRaf = null;
  let cartPricingCoalesceCart = null;

  const scheduleCartPricing = (cart) => {
    if (cartPricingRaf) cancelAnimationFrame(cartPricingRaf);
    cartPricingRaf = requestAnimationFrame(() => {
      cartPricingRaf = null;
      document.dispatchEvent(new CustomEvent('ghalia:cart:pricing', { detail: { cart } }));
    });
  };

  const scheduleCartPricingCoalesced = (cart) => {
    if (!cart) return;
    cartPricingCoalesceCart = cart;
    if (cartPricingRaf) return;
    cartPricingRaf = requestAnimationFrame(() => {
      cartPricingRaf = null;
      const next = cartPricingCoalesceCart;
      cartPricingCoalesceCart = null;
      if (next) scheduleCartPricing(next);
    });
  };

  const paintAllCartLinePrices = (cart) => {
    (cart?.items || []).forEach((item) => paintCartLinePrice(cart, item.key));
  };

  const paintCartLinePrice = (cart, key) => {
    const item = (cart?.items || []).find((row) => row.key === key);
    const line = getCartLineRoot(key);
    const pricing = window.GhaliaPricing;
    if (!item || !line || !pricing) return;
    if (pricing.isFreeGiftLine?.(item)) return;
    const currentEl = line.querySelector('[data-cart-line-current]');
    const compareEl = line.querySelector('[data-cart-line-compare]');
    const originalNet = Number(item.original_line_price) || Number(item.line_price) || 0;
    const allocNet = (item.line_level_discount_allocations || []).reduce(
      (sum, alloc) => sum + (Number(alloc.amount) || 0),
      0
    );
    const finalNet =
      allocNet > 0
        ? Math.max(0, originalNet - allocNet)
        : Number(item.final_line_price) || originalNet;
    if (currentEl) {
      const nextPrice = pricing.formatCustomerLineNet
        ? pricing.formatCustomerLineNet(finalNet, line)
        : pricing.formatNet(finalNet);
      if (currentEl.textContent.trim() !== nextPrice.trim()) {
        currentEl.textContent = nextPrice;
      }
    }
    if (compareEl) {
      if (originalNet > finalNet && originalNet > 0) {
        const compareText = pricing.formatCustomerLineNet
          ? pricing.formatCustomerLineNet(originalNet, line)
          : pricing.formatNet(originalNet);
        compareEl.textContent = compareText;
        compareEl.hidden = false;
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.hidden = true;
        compareEl.setAttribute('hidden', '');
      }
    }
  };

  const catalogNetFromCart = (cart, drawer) => {
    const pricing = window.GhaliaPricing;
    if (!pricing || !drawer || !cart?.items) return 0;
    let net = 0;
    cart.items.forEach((item) => {
      if (pricing.isFreeGiftLine?.(item)) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      const qty = Number(item.quantity) || 1;
      const fromItem = Number(item.original_line_price) || Number(item.line_price) || 0;
      if (fromItem > 0) {
        net += fromItem;
        return;
      }
      const domNet = Number(row?.dataset?.cartLineNet || 0);
      const domQty = Number(row?.querySelector('[data-cart-line-qty]')?.value) || qty;
      if (domNet > 0) {
        net += domQty > 0 && domQty !== qty ? Math.round(domNet * (qty / domQty)) : domNet;
        return;
      }
      net += Number(item.original_line_price) || Number(item.line_price) || 0;
    });
    return net;
  };

  const patchStackStateNetFromCart = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const prev = window.GhaliaCart?.lastStackState || {};
    if (!drawer || !cart?.items) return;
    const net = catalogNetFromCart(cart, drawer);
    window.GhaliaCart = window.GhaliaCart || {};
    window.GhaliaCart.lastStackState = { ...prev, netSubtotalCents: net };
  };

  const refreshCartSummaryLight = (cart) => {
    patchStackStateNetFromCart(cart);
    const state = window.GhaliaCart?.lastStackState;
    const drawer = document.querySelector('[data-cart-drawer]');
    if (cart && state && drawer && window.GhaliaCartSummary?.apply) {
      window.GhaliaCartSummary.apply(cart, state, drawer);
    }
  };

  const repaintCartLinePromoPrices = (cart) => {
    if (!cart) return;
    refreshCartSummaryLight(cart);
    window.GhaliaCartStackDiscount?.repaintCart?.(cart);
  };

  const applyCartLocally = (cart, options = {}) => {
    if (!cart) return;
    if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
    renderCart(cart);
    if (options.skipPricing) {
      refreshCartSummaryLight(cart);
    } else {
      scheduleCartPricing(cart);
    }
    if (options.promoUi !== false) syncCartPromoUI(cart);
    syncCartCheckoutState();
  };

  const optimisticCartLineQty = (key, qty) => {
    const line = getCartLineRoot(key);
    if (!line) return;
    const input = line.querySelector('[data-cart-line-qty]');
    if (input) input.value = String(Math.max(0, qty));
    line.classList.toggle('cart-line--removing', qty <= 0);
  };

  const syncCartLineFromItem = (item, cart) => {
    const line = getCartLineRoot(item.key);
    if (!line || !item) return;
    const input = line.querySelector('[data-cart-line-qty]');
    const qty = Math.max(1, Number(item.quantity) || 1);
    if (input) input.value = String(item.quantity);
    const pricing = window.GhaliaPricing;
    const finalNet = Number(item.final_line_price ?? item.line_price ?? 0);
    const catalogNet =
      Number(item.original_line_price) ||
      Number(item.line_price) ||
      Number(item.original_price ?? item.price ?? 0) * qty ||
      finalNet;
    if (catalogNet > 0) {
      line.dataset.cartLineNet = String(catalogNet);
      line.dataset.cartLineUnitNet = String(Math.round(catalogNet / qty));
    }
    if (pricing?.displayCents) {
      const displayNet =
        catalogNet > finalNet && finalNet >= 0 ? finalNet : catalogNet > 0 ? catalogNet : 0;
      if (displayNet > 0) {
        const display = pricing.displayCents(displayNet);
        line.dataset.cartLineDisplay = String(display);
        line.dataset.cartLineCustomerNet = String(displayNet);
        line.dataset.cartLineUnitDisplay = String(Math.round(display / qty));
      }
    }
    if (catalogNet > finalNet && finalNet >= 0 && pricing?.displayCents) {
      line.dataset.cartLineCompareDisplay = String(pricing.displayCents(catalogNet));
    } else {
      delete line.dataset.cartLineCompareDisplay;
    }
  };

  const syncCartLinesFromServer = (cart) => {
    (cart?.items || []).forEach((item) => syncCartLineFromItem(item, cart));
  };

  const applyOptimisticLineQty = (key, quantity) => {
    const line = getCartLineRoot(key);
    if (line && quantity > 0) {
      const prevQty = Number(line.querySelector('[data-cart-line-qty]')?.value) || quantity;
      const prevNet = Number(line.dataset.cartLineNet || 0);
      if (prevNet > 0 && prevQty > 0 && prevQty !== quantity) {
        const unitNet = Math.round(prevNet / prevQty);
        const nextNet = unitNet * quantity;
        line.dataset.cartLineNet = String(nextNet);
        line.dataset.cartLineUnitNet = String(unitNet);
        const pricing = window.GhaliaPricing;
        if (pricing?.displayCents) {
          const prevDisplay = Number(line.dataset.cartLineDisplay || 0);
          if (prevDisplay > 0) {
            const unitDisplay = Math.round(prevDisplay / prevQty);
            line.dataset.cartLineDisplay = String(unitDisplay * quantity);
            line.dataset.cartLineUnitDisplay = String(unitDisplay);
          }
          const prevCustomerNet = Number(line.dataset.cartLineCustomerNet || 0);
          if (prevCustomerNet > 0) {
            const unitCustomer = Math.round(prevCustomerNet / prevQty);
            line.dataset.cartLineCustomerNet = String(unitCustomer * quantity);
          }
        }
      }
    }
    optimisticCartLineQty(key, quantity);
    const optimistic = patchCartLineQuantity(window.GhaliaCart?.lastCart, key, quantity);
    if (window.GhaliaCart) window.GhaliaCart.lastCart = optimistic;
    updateDrawerTitle(optimistic);
    renderCart(optimistic);
    updateGiftBanner(optimistic);
    syncCartPromoUI(optimistic);
    window.GhaliaCartStackDiscount?.resetFingerprint?.();
    scheduleCartPricingCoalesced(optimistic);
    return optimistic;
  };

  const applyServerCartAfterQtyChange = (synced, changedKey) => {
    if (!synced) return;
    if (window.GhaliaCart) window.GhaliaCart.lastCart = synced;
    pruneRemovedCartLines(synced);
    const displayCart = patchCartGiftState(synced);
    if (changedKey && synced.items?.some((item) => item.key === changedKey)) {
      const item = synced.items.find((row) => row.key === changedKey);
      syncCartLineFromItem(item, synced);
    } else {
      syncCartLinesFromServer(synced);
    }
    updateGiftBanner(displayCart);
    updateDrawerTitle(synced);
    if (!synced.items?.length) showCartDrawerEmpty();
    syncCartPromoUI(synced);
    syncCartCheckoutState();
    window.GhaliaCartStackDiscount?.resetFingerprint?.();
    scheduleCartPricingCoalesced(synced);
    ensureCartLinesRendered(synced).catch(() => {});
    return synced;
  };

  const applyServerCartAfterGiftPatch = async (synced) => {
    if (!synced) return;
    if (window.GhaliaCart) window.GhaliaCart.lastCart = synced;
    const next = patchCartGiftState(synced);
    await ensureCartLinesRendered(synced);
    updateGiftBanner(next);
    updateDrawerTitle(synced);
    syncCartPromoUI(synced);
    syncCartCheckoutState();
    scheduleCartPricingCoalesced(synced);
    return synced;
  };

  const finishCartClientRefresh = (cart, beforeCart) => {
    if (!cart) return cart;
    if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
    syncCartLinesFromServer(cart);
    updateDrawerTitle(cart);
    renderCart(cart);
    updateGiftBanner(patchCartGiftState(cart));
    syncCartPromoUI(cart);
    syncCartCheckoutState();
    scheduleCartPricingCoalesced(cart);
    if (cart?.items?.length) {
      scheduleDeferredCartSync({ beforeCart, cart, reason: 'add' });
    }
    if (!cartDrawerLinesMatchCart(cart)) {
      ensureCartLinesRendered(cart).catch(() => {});
    }
    return cart;
  };

  const needsFullDrawerRefresh = (before, after) => {
    if (!after?.items?.length) return true;
    const beforeKeys = new Set((before?.items || []).map((item) => item.key));
    const afterKeys = new Set(after.items.map((item) => item.key));
    for (const key of beforeKeys) {
      if (!afterKeys.has(key)) return true;
    }
    for (const item of after.items) {
      if (!beforeKeys.has(item.key)) return true;
    }
    return false;
  };

  const onlyNewGiftLinesAdded = (before, after, config) => {
    if (!after?.items?.length || !config) return false;
    const beforeKeys = new Set((before?.items || []).map((item) => item.key));
    const afterKeys = new Set(after.items.map((item) => item.key));
    for (const key of beforeKeys) {
      if (!afterKeys.has(key)) return false;
    }
    const added = after.items.filter((item) => !beforeKeys.has(item.key));
    if (!added.length) return false;
    return added.every((item) => isConfiguredFreeGiftLine(item, config));
  };

  const needsStructuralDrawerRefresh = (before, after, config) => {
    if (!needsFullDrawerRefresh(before, after)) return false;
    return !onlyNewGiftLinesAdded(before, after, config);
  };

  const formatThresholdMoney = (cents) => {
    const P = window.GhaliaPricing;
    if (P?.formatNet) return P.formatNet(cents);
    return (Number(cents) / 100).toFixed(0);
  };

  const updateGiftBanner = (cart) => {
    const banner = cartDrawer?.querySelector('[data-cart-gift-banner]');
    if (!banner) return;
    const config = getCartGiftConfig();
    if (!config) return;
    const qualifying = getQualifyingCartTotal(cart, config);
    const threshold = config.threshold;
    const msg = banner.querySelector('.cart-gift__msg');
    const hasPaid = (cart?.items || []).some(
      (item) => !isGiftCatalogLine(item, config) && !hasFreeGiftProperty(item)
    );
    const unlocked = config.alwaysGift ? hasPaid : qualifying >= threshold;
    banner.classList.toggle('is-unlocked', unlocked);
    if (!msg) return;
    if (unlocked) {
      msg.innerHTML = banner.dataset.giftUnlockedHtml || msg.innerHTML;
      return;
    }
    const remaining = Math.max(0, threshold - qualifying);
    const template = banner.dataset.giftSpendTemplate || '';
    msg.innerHTML = template.replace('__AMOUNT__', formatThresholdMoney(remaining));
  };

  const patchCartGiftState = (cart) => {
    const config = getCartGiftConfig();
    if (!config || !cart?.items) return cart;
    const wrongGiftLines = findWrongFreeGiftLines(cart, config);
    const orphanCatalogLines = findOrphanCatalogGiftLines(cart, config);
    const giftLine = findConfiguredFreeGiftLine(cart, config);
    const qualifying = getQualifyingCartTotal(cart, config);
    const hasPaid = cart.items.some(
      (item) => !isGiftCatalogLine(item, config) && !hasFreeGiftProperty(item)
    );
    const shouldHave = config.alwaysGift ? hasPaid : qualifying >= config.threshold;
    const removeKeys = new Set([
      ...wrongGiftLines.map((item) => item.key),
      ...orphanCatalogLines.map((item) => item.key),
      ...(shouldHave ? [] : giftLine ? [giftLine.key] : []),
    ]);
    if (!removeKeys.size) return cart;
    const next = cloneCart(cart);
    next.items = next.items.filter((item) => !removeKeys.has(item.key));
    next.item_count = cartItemTotal(next);
    return next;
  };

  let cartEmptyTemplateHtml = '';

  const cacheCartEmptyTemplate = () => {
    if (cartEmptyTemplateHtml) return;
    const tpl = cartDrawer?.querySelector('[data-cart-empty-template]');
    if (tpl) cartEmptyTemplateHtml = tpl.innerHTML.trim();
  };

  const showCartDrawerEmpty = () => {
    const body = cartDrawer?.querySelector('[data-cart-drawer-body]');
    if (!body) return;
    cacheCartEmptyTemplate();
    cartDrawer?.querySelector('[data-cart-drawer-footer], .drawer__footer')?.remove();
    if (cartEmptyTemplateHtml) {
      body.innerHTML = cartEmptyTemplateHtml;
      return;
    }
    if (body.querySelector('.cart-empty')) return;
    const emptyCopy = window.Shopify?.strings?.cart?.empty || 'Your bag is empty.';
    const continueCopy = window.Shopify?.strings?.cart?.continueShopping || 'Continue shopping';
    const root = window.Shopify?.routes?.root || '/';
    body.innerHTML = `<div class="cart-empty"><p>${emptyCopy}</p><a class="btn btn--primary" href="${root}collections" data-drawer-close>${continueCopy}</a></div>`;
  };

  const applyOptimisticCartState = (cart, options = {}) => {
    const displayCart = patchCartGiftState(cart);
    pruneRemovedCartLines(cart);
    updateGiftBanner(displayCart);
    updateDrawerTitle(cart);
    if (!cart?.items?.length) showCartDrawerEmpty();
    if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
    renderCart(cart);
    if (options.promoUi !== false) syncCartPromoUI(cart);
    syncCartCheckoutState();
    return cart;
  };

  const updateDrawerTitle = (cart) => {
    const title = cartDrawer?.querySelector('.drawer__title');
    if (!title) return;
    const count = getCartDisplayCount(cart);
    const template = title.dataset.titleTemplate || title.textContent.replace(/\d+/, '__COUNT__');
    if (!title.dataset.titleTemplate && /\d+/.test(title.textContent)) {
      title.dataset.titleTemplate = title.textContent.replace(/\d+/, '__COUNT__');
    }
    if (title.dataset.titleTemplate) {
      title.textContent = title.dataset.titleTemplate.replace('__COUNT__', String(count));
    }
  };

  const syncCartCheckoutState = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const banner = drawer.querySelector('[data-cart-oos-banner]');
    const checkoutBtn = drawer.querySelector('.cart-checkout');
    const hasOos = drawer.querySelector('[data-cart-line-available="false"]') != null;
    if (banner) banner.hidden = !hasOos;
    if (checkoutBtn) {
      checkoutBtn.removeAttribute('aria-busy');
      checkoutBtn.disabled = hasOos;
      checkoutBtn.setAttribute('aria-disabled', hasOos ? 'true' : 'false');
    }
  };

  const fetchVariantStock = async (variantId) => {
    const id = String(variantId);
    if (variantStockCache.has(id)) return variantStockCache.get(id);
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}variants/${id}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not verify availability');
    const data = await res.json();
    const stock = {
      available: Boolean(data.available),
      inventoryManagement: data.inventory_management,
      inventoryQuantity: Number(data.inventory_quantity),
    };
    variantStockCache.set(id, stock);
    return stock;
  };

  const assertVariantCanAdd = async (variantId, quantity = 1) => {
    const stock = await fetchVariantStock(variantId);
    const soldOut = window.Shopify?.strings?.cart?.soldOut || 'Sold out';
    if (!stock.available) throw new Error(soldOut);
    if (
      stock.inventoryManagement === 'shopify' &&
      Number.isFinite(stock.inventoryQuantity) &&
      quantity > stock.inventoryQuantity
    ) {
      const maxMsg =
        window.Shopify?.strings?.cart?.maxQuantityReached ||
        `Only ${stock.inventoryQuantity} available`;
      throw new Error(stock.inventoryQuantity > 0 ? maxMsg : soldOut);
    }
    return stock;
  };

  const getCartMaxItems = () => {
    const max = parseInt(cartDrawer?.dataset.cartMaxItems || '0', 10);
    return Number.isFinite(max) && max > 0 ? max : 0;
  };

  const cartItemTotal = (cart) =>
    (cart?.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const hasPendingCartLineMutations = () => cartLineMutations.size > 0;
  let cartAddsInFlight = 0;
  const hasPendingCartAdds = () => cartAddsInFlight > 0;
  const hasPendingCartMutations = () => hasPendingCartLineMutations() || hasPendingCartAdds();
  let pendingReconcile = null;
  let reconcileFlushPending = false;

  const queuePendingReconcileFlush = () => {
    if (reconcileFlushPending) return;
    reconcileFlushPending = true;
    queueMicrotask(() => {
      reconcileFlushPending = false;
      if (!pendingReconcile || hasPendingCartMutations()) return;
      const job = pendingReconcile;
      pendingReconcile = null;
      cartReconcileChain = cartReconcileChain
        .then(() => runReconcileJob(job))
        .catch((err) => console.error(err));
    });
  };

  const finishCartAddMutation = (state = {}) => {
    cartAddsInFlight = Math.max(0, cartAddsInFlight - 1);
    flushDeferredPromoSync();
    queuePendingReconcileFlush();
    if (state.openDrawerAfter && state.cartDrawerEl) openDrawer(state.cartDrawerEl);
  };

  const flushDeferredPromoSync = () => {
    if (hasPendingCartMutations()) return;
    const cart = window.GhaliaCart?.lastCart;
    if (!cart || promoSyncBusy) return;
    if (promoNeedsRestore(cart)) scheduleImmediatePromoSync(cart);
  };

  const cartMutationFingerprint = (cart) => {
    if (!cart?.items?.length) return `empty:${cart?.item_count || 0}`;
    return cart.items
      .map(
        (item) =>
          `${item.key}:${item.quantity}:${item.final_line_price || item.line_price || 0}`
      )
      .join('|');
  };

  let cartReconcileChain = Promise.resolve();
  let cartMutationChain = Promise.resolve();
  const lineQtyChains = new Map();

  const runCartMutation = (fn) => {
    const next = cartMutationChain.then(() => fn());
    cartMutationChain = next.catch(() => {});
    return next;
  };

  const CART_DRAWER_SECTION = 'cart-drawer';

  const codePromoAllocationNet = (cart) =>
    window.GhaliaPromoConfig?.codePromoAllocationNet?.(cart, cartDrawer) ??
    (cart?.items || []).reduce(
      (sum, item) =>
        sum +
        (item.line_level_discount_allocations || []).reduce((lineSum, alloc) => {
          if (!isPromoCodeApplication(alloc?.discount_application)) return lineSum;
          return lineSum + (Number(alloc.amount) || 0);
        }, 0),
      0
    );

  const promoNeedsRestore = (cart) => {
    const code = storedPromoCode() || defaultPromoCode();
    if (!code) return false;
    if (!cartPromoMeetsMinimum(cart)) return false;
    if (cartPromoCode(cart)) return false;
    if (lineHasPromoCodeAllocation(cart)) return false;
    if (codePromoAllocationNet(cart) > 0) return false;
    return true;
  };

  const giftStateChanged = (before, after) => {
    const config = getCartGiftConfig();
    if (!config) return false;
    if (giftSyncFingerprint(before, config) !== giftSyncFingerprint(after, config)) return true;
    const hadGift = Boolean(findConfiguredFreeGiftLine(before, config));
    const hasGift = Boolean(findConfiguredFreeGiftLine(after, config));
    if (hadGift !== hasGift) return true;
    const qBefore = getQualifyingCartTotal(before, config);
    const qAfter = getQualifyingCartTotal(after, config);
    const shouldBefore = config.alwaysGift
      ? (before?.items || []).some((item) => !isFreeGiftLine(item, config))
      : qBefore >= config.threshold;
    const shouldAfter = config.alwaysGift
      ? (after?.items || []).some((item) => !isFreeGiftLine(item, config))
      : qAfter >= config.threshold;
    return shouldBefore !== shouldAfter;
  };

  const applyCartLineChangeFast = (beforeCart, synced, options = {}) => {
    const displayCart = patchCartGiftState(synced);
    pruneRemovedCartLines(synced);
    syncCartLinesFromServer(synced);
    updateGiftBanner(displayCart);
    updateDrawerTitle(synced);
    if (!synced?.items?.length) showCartDrawerEmpty();
    applyCartLocally(synced, { skipPricing: true, promoUi: false });
    syncCartPromoUI(synced);
    scheduleCartPricingCoalesced(synced);
    if (options.syncPromo !== false && promoNeedsRestore(synced) && !hasPendingCartMutations()) {
      scheduleImmediatePromoSync(synced);
    }
    if (!cartDrawerLinesMatchCart(synced)) {
      ensureCartLinesRendered(synced).catch(() => {});
    }
    return synced;
  };

  const hasDuplicatePaidCartLines = (cart) => {
    if (!cart?.items?.length) return false;
    const config = getCartGiftConfig();
    const groups = new Map();
    (cart.items || []).forEach((item) => {
      if (hasFreeGiftProperty(item)) return;
      if (config && isGiftCatalogLine(item, config)) return;
      const planId = item.selling_plan_allocation?.selling_plan?.id || '';
      const sig = `${item.variant_id}|${planId}|${linePropertiesSignature(item)}`;
      groups.set(sig, (groups.get(sig) || 0) + 1);
    });
    return [...groups.values()].some((count) => count > 1);
  };

  let deferredSyncTimer = null;
  let deferredSyncPending = null;

  const runCartUiSync = (cart) => {
    if (!cart) return;
    scheduleCartPricingCoalesced(cart);
    document.dispatchEvent(new CustomEvent('ghalia:cart:ui-sync', { detail: { cart } }));
  };

  const runDeferredBackgroundSync = async ({
    beforeCart = null,
    cart: inputCart = null,
    reason = 'mutation',
  } = {}) => {
    if (hasPendingCartMutations()) return window.GhaliaCart?.lastCart;
    let latest = inputCart || window.GhaliaCart?.lastCart;
    if (!latest?.items?.length) return latest;

    const startCart = latest;
    const drawer = document.querySelector('[data-cart-drawer]');

    if (hasDuplicatePaidCartLines(latest)) {
      latest = (await consolidateDuplicatePaidLines(latest)) || latest;
      if (window.GhaliaCart) window.GhaliaCart.lastCart = latest;
      syncCartLinesFromServer(latest);
      updateDrawerTitle(latest);
    }

    const config = getCartGiftConfig();
    if (config && giftNeedsNetworkSync(latest, config)) {
      latest = await syncCartFreeGift(latest);
    }

    if (window.GhaliaQualifyingBasket?.syncAttribute) {
      latest = (await window.GhaliaQualifyingBasket.syncAttribute(latest, drawer)) || latest;
    }

    const giftAction = config ? giftNeedsAction(latest, config) : { needsSync: false };
    const drawerPatched = canSkipDrawerRefresh(latest);
    const structuralChange = beforeCart && needsStructuralDrawerRefresh(beforeCart, latest, config);
    const cartMutated = latest !== startCart;
    const needsDrawer =
      reason !== 'drawer-refresh' &&
      (giftAction.needsSync ||
        (structuralChange && !drawerPatched) ||
        (!cartDrawerLinesMatchCart(latest) && !drawerPatched));

    if (needsDrawer) {
      suppressDrawerRefreshHandler = true;
      try {
        await fetchCartDrawer();
      } finally {
        suppressDrawerRefreshHandler = false;
      }
      latest = window.GhaliaCart?.lastCart || latest;
    } else if (cartMutated) {
      await applyServerCartAfterGiftPatch(latest);
    } else if (window.GhaliaCart) {
      window.GhaliaCart.lastCart = latest;
    }

    runCartUiSync(latest);
    syncCartPromoUI(latest);
    syncCartCheckoutState();
    return latest;
  };

  const cancelDeferredCartSync = () => {
    clearTimeout(deferredSyncTimer);
    deferredSyncTimer = null;
    deferredSyncPending = null;
  };

  const scheduleDeferredCartSync = (payload = {}) => {
    deferredSyncPending = {
      beforeCart: payload.beforeCart ?? deferredSyncPending?.beforeCart ?? null,
      cart: payload.cart ?? deferredSyncPending?.cart ?? window.GhaliaCart?.lastCart,
      reason: payload.reason || deferredSyncPending?.reason || 'mutation',
    };
    clearTimeout(deferredSyncTimer);
    const delay =
      payload.reason === 'drawer-refresh' ? 600 : payload.reason === 'qty' ? 1200 : 1500;
    deferredSyncTimer = window.setTimeout(() => {
      deferredSyncTimer = null;
      const job = deferredSyncPending;
      deferredSyncPending = null;
      if (!job) return;
      if (hasPendingCartMutations()) {
        pendingReconcile = job;
        queuePendingReconcileFlush();
        return;
      }
      cartReconcileChain = cartReconcileChain
        .then(() => runDeferredBackgroundSync(job))
        .catch((err) => console.error('[GhaliaCartCoordinator]', err));
    }, delay);
  };

  const runReconcileJob = (job) => runDeferredBackgroundSync(job);

  const reconcileCartAfterLineChange = (beforeCart, serverCart) => {
    scheduleDeferredCartSync({ beforeCart, cart: serverCart, reason: 'qty' });
    return cartReconcileChain;
  };

  const syncLineQtyToServer = async (key) => {
    const pending = cartLineMutations.get(key);
    if (!pending) return;

    try {
      while (true) {
        const qty = pending.targetQty;
        const beforeCart = window.GhaliaCart?.lastCart;

        const maxItems = getCartMaxItems();
        if (maxItems > 0 && qty > 0) {
          const line = (beforeCart?.items || []).find((item) => item.key === key);
          const lineQty = Number(line?.quantity) || 0;
          const otherTotal = cartItemTotal(beforeCart) - lineQty;
          if (otherTotal + qty > maxItems) {
            showGhaliaToast(`Bag limit is ${maxItems} items`);
            const allowed = Math.max(0, maxItems - otherTotal);
            pending.targetQty = allowed;
            if (allowed > 0) applyOptimisticLineQty(key, allowed);
            else {
              optimisticCartLineQty(key, 0);
              applyOptimisticCartState(
                patchCartLineQuantity(window.GhaliaCart?.lastCart, key, 0),
                { promoUi: false }
              );
            }
            break;
          }
        }

        const synced = await GhaliaCartApi.changeItem({ id: key, quantity: qty });
        if (pending.targetQty !== qty) continue;

        applyServerCartAfterQtyChange(synced, key);
        reconcileCartAfterLineChange(beforeCart, synced);
        if (pending.targetQty === qty) break;
      }
    } catch (err) {
      console.error(err);
      showGhaliaToast(err.message || 'Could not update bag');
      try {
        applyServerCartAfterQtyChange(await fetchCart(), key);
      } catch (_) { /* noop */ }
    } finally {
      getCartLineRoot(key)?.classList.remove('cart-line--removing');
      const still = cartLineMutations.get(key);
      if (!still || still !== pending) return;
      if (still.targetQty === pending.targetQty) {
        cartLineMutations.delete(key);
      } else {
        flushCartLineQty(key);
        return;
      }
      if (!hasPendingCartMutations()) {
        const cart = window.GhaliaCart?.lastCart;
        if (cart && hasDuplicatePaidCartLines(cart)) {
          consolidateDuplicatePaidLines(cart)
            .then((merged) => {
              if (!merged || merged === cart) {
                flushDeferredPromoSync();
                return;
              }
              applyServerCartAfterQtyChange(merged, null);
              flushDeferredPromoSync();
            })
            .catch(() => flushDeferredPromoSync());
        } else {
          flushDeferredPromoSync();
        }
      }
    }
  };

  const flushCartLineQty = (key) => {
    const prev = lineQtyChains.get(key) || Promise.resolve();
    const next = prev.then(() => syncLineQtyToServer(key)).catch(() => {});
    lineQtyChains.set(key, next);
  };

  const queueCartLineChange = (key, quantity) => {
    if (quantity <= 0) {
      removeCartLineNow(key);
      return;
    }

    const maxItems = getCartMaxItems();
    if (maxItems > 0 && quantity > 0) {
      const cart = window.GhaliaCart?.lastCart;
      const line = (cart?.items || []).find((item) => item.key === key);
      const lineQty = Number(line?.quantity) || 0;
      if (cartItemTotal(cart) - lineQty + quantity > maxItems) {
        showGhaliaToast(`Bag limit is ${maxItems} items`);
        return;
      }
    }

    let pending = cartLineMutations.get(key);
    if (!pending) {
      pending = { targetQty: quantity };
      cartLineMutations.set(key, pending);
    } else {
      pending.targetQty = quantity;
    }

    applyOptimisticLineQty(key, quantity);
    flushCartLineQty(key);
  };

  const removeCartLineNow = (key) => {
    let pending = cartLineMutations.get(key);
    if (!pending) {
      pending = { targetQty: 0 };
      cartLineMutations.set(key, pending);
    } else {
      pending.targetQty = 0;
    }

    optimisticCartLineQty(key, 0);
    const optimistic = patchCartLineQuantity(window.GhaliaCart?.lastCart, key, 0);
    applyOptimisticCartState(optimistic, { promoUi: false });
    updateDrawerTitle(optimistic);
    flushCartLineQty(key);
  };

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn || btn.disabled) return;
    const cartLine = btn.closest('[data-cart-line-key]');
    if (cartLine) {
      e.preventDefault();
      e.stopPropagation();
      const input = cartLine.querySelector('[data-cart-line-qty]');
      if (!input || input.disabled) return;
      const key = cartLine.dataset.cartLineKey;
      const step = parseInt(btn.dataset.qtyStep, 10);
      const min = parseInt(input.min || '0', 10);
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const pending = cartLineMutations.get(key);
      const current = pending?.targetQty ?? (parseInt(input.value, 10) || 0);
      const next = Math.max(min, Math.min(max, current + step));
      if (next === current) {
        if (Number.isFinite(max) && current >= max) {
          showGhaliaToast(
            window.Shopify?.strings?.cart?.maxQuantityReached || 'Maximum available quantity reached'
          );
        }
        return;
      }
      queueCartLineChange(key, next);
      return;
    }
    const root = btn.closest('.qty');
    const input = root?.querySelector('input');
    if (!input) return;
    const step = parseInt(btn.dataset.qtyStep, 10);
    const value = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) + step);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const normalizeHeaderCount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };

  const getCartDisplayCount = (cart) => {
    if (!cart) return 0;
    if (Array.isArray(cart.items) && cart.items.length) {
      let sum = 0;
      cart.items.forEach((item) => {
        sum += Number(item.quantity) || 0;
      });
      if (sum > 0) return sum;
    }
    return normalizeHeaderCount(cart.item_count);
  };

  let cartBadgeSyncing = false;

  const scrubCartHeaderBtn = (count) => {
    if (cartBadgeSyncing) return;
    const btn = document.querySelector('[data-cart-header-btn]');
    if (!btn) return;
    const total = normalizeHeaderCount(count);
    cartBadgeSyncing = true;
    try {
      btn.querySelectorAll(':scope > *:not(svg.icon)').forEach((el) => {
        if (total === 0 || !el.hasAttribute('data-cart-count')) el.remove();
      });
      if (total === 0) btn.querySelectorAll('[data-cart-count], .site-actions__count').forEach((el) => el.remove());
    } finally {
      cartBadgeSyncing = false;
    }
  };

  /** Mount badge only when count ≥ 1; remove from DOM when 0 (Missoma-style). */
  const syncHeaderCountBadge = ({ parentSelector, badgeAttr, count }) => {
    const parent = document.querySelector(parentSelector);
    if (!parent) return;

    const total = normalizeHeaderCount(count);
    if (parent.matches('[data-cart-header-btn]')) scrubCartHeaderBtn(total);

    let badge = parent.querySelector(`[${badgeAttr}]`);

    if (total === 0) {
      badge?.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'site-actions__count';
      badge.setAttribute(badgeAttr, '');
      parent.appendChild(badge);
    }

    badge.textContent = String(total);
    badge.setAttribute('data-count', String(total));
    badge.removeAttribute('hidden');
    badge.setAttribute('aria-hidden', 'false');
  };

  const setActionCount = (selector, count) => {
    const total = normalizeHeaderCount(count);
    $$(selector).forEach((el) => {
      const parent = el.closest('[data-cart-header-btn], [data-wishlist-header-btn]');
      if (parent) {
        const attr = el.hasAttribute('data-cart-count') ? 'data-cart-count' : 'data-wishlist-count';
        const sel = parent.hasAttribute('data-cart-header-btn') ? '[data-cart-header-btn]' : '[data-wishlist-header-btn]';
        syncHeaderCountBadge({ parentSelector: sel, badgeAttr: attr, count: total });
        return;
      }
      if (total === 0) {
        el.remove();
        return;
      }
      el.textContent = String(total);
      el.setAttribute('data-count', String(total));
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
    });
  };

  const syncSwymWishlistCounter = (count) => {
    $$('.swym-wishlist-counter, .site-actions__btn--wishlist [class*="swym"][class*="count"]').forEach((el) => {
      const visible = count > 0;
      el.hidden = !visible;
      el.classList.toggle('is-empty', !visible);
      if (visible) {
        el.textContent = String(count);
        el.setAttribute('data-count', String(count));
      } else {
        el.textContent = '';
        el.removeAttribute('data-count');
      }
    });
  };

  const renderCart = (cart) => {
    syncHeaderCountBadge({
      parentSelector: '[data-cart-header-btn]',
      badgeAttr: 'data-cart-count',
      count: getCartDisplayCount(cart),
    });
    document.dispatchEvent(new CustomEvent('ghalia:cart:update', { detail: cart }));
  };

  const getNativeFetch = () => window.__GhaliaNet?.fetch || window.fetch.bind(window);
  const NativeXHR = () => window.__GhaliaNet?.XHR || window.XMLHttpRequest;

  const fetchCartJson = (url, options = {}) =>
    new Promise((resolve, reject) => {
      const xhr = new (NativeXHR())();
      xhr.open(options.method || 'GET', url, true);
      xhr.setRequestHeader('Accept', 'application/json');
      if (options.body) xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      xhr.onload = () => {
        const text = xhr.responseText || '';
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: async () => JSON.parse(text || 'null'),
          text: async () => text,
          clone() {
            return this;
          },
        });
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(options.body || null);
    });

  let fetchCartInflight = null;
  let fetchCartLastAt = 0;
  let cartApiBlockedUntil = 0;

  const blockCartApi = (ms = 30000) => {
    cartApiBlockedUntil = Math.max(cartApiBlockedUntil, Date.now() + ms);
  };

  const cartApiIsBlocked = () => Date.now() < cartApiBlockedUntil;

  const fetchCart = async (options = {}) => {
    const force = options.force === true;
    if (!force && cartApiIsBlocked()) {
      if (window.GhaliaCart?.lastCart) return window.GhaliaCart.lastCart;
      throw new Error('Could not load bag');
    }
    const now = Date.now();
    if (!force && fetchCartInflight) return fetchCartInflight;
    if (!force && now - fetchCartLastAt < 800 && window.GhaliaCart?.lastCart) {
      return window.GhaliaCart.lastCart;
    }
    fetchCartInflight = (async () => {
      const url = `${window.Shopify?.routes?.root || '/'}cart.js`;
      const opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      let res;
      try {
        res = await getNativeFetch()(url, opts);
      } catch (_) {
        res = await fetchCartJson(url);
      }
      if (res.status === 429) {
        blockCartApi();
        throw new Error('Could not load bag');
      }
      if (!res.ok) {
        try {
          res = await fetchCartJson(url);
        } catch (err) {
          throw new Error('Could not load bag');
        }
      }
      if (res.status === 429) {
        blockCartApi();
        throw new Error('Could not load bag');
      }
      if (!res.ok) throw new Error('Could not load bag');
      fetchCartLastAt = Date.now();
      return res.json();
    })();
    try {
      return await fetchCartInflight;
    } finally {
      fetchCartInflight = null;
    }
  };

  const normalizeVariantId = (variantId) => {
    const n = Number(variantId);
    return Number.isFinite(n) && n > 0 ? n : variantId;
  };

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const isPromoCodeApplication = (app) =>
    window.GhaliaPromoConfig?.isDiscountCodeApplication?.(app) ?? app?.type === 'discount_code';

  const promoCodeFromLineAllocations = (cart) => {
    for (const item of cart?.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        if (!isPromoCodeApplication(alloc?.discount_application)) continue;
        const title = alloc.discount_application?.title;
        if (title) return String(title).trim();
      }
    }
    return '';
  };

  const cartPromoCode = (cart) =>
    window.GhaliaPromoConfig?.cartDiscountCodeTitle?.(cart) ||
    (() => {
      const apps = cart?.cart_level_discount_applications || [];
      const codeApp = apps.find((d) => isPromoCodeApplication(d));
      if (codeApp?.title) return String(codeApp.title).trim();
      return promoCodeFromLineAllocations(cart);
    })();

  /** Keep an active Shopify discount on cart/update.js (mutations otherwise drop codes). */
  const discountCodeToPreserve = (cart) => {
    const active = cartPromoCode(cart);
    if (active) return active;
    if (promoAppliedOnShopify(cart)) {
      const stored = storedPromoCode();
      if (stored) return stored;
    }
    return '';
  };

  const withPreservedDiscount = (body, cart) => {
    const code = discountCodeToPreserve(cart);
    if (code) body.discount = code;
    return body;
  };

  const cartHasPromoDiscount = (cart, drawer) => {
    if ((cart?.cart_level_discount_applications || []).length > 0) return true;
    if (Number(cart?.total_discount) > 0) return true;
    const pricing = window.GhaliaPricing;
    return (cart?.items || []).some((item) => {
      if (pricing?.isNativeBundleLine?.(item) || pricing?.isFreeGiftLine?.(item)) return false;
      if ((item.line_level_discount_allocations || []).length > 0) return true;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const domNet = Number(row?.dataset?.cartLineNet || 0);
      const finalNet = Number(item.final_line_price) || 0;
      return domNet > 0 && finalNet > 0 && finalNet < domNet;
    });
  };

  const storedPromoCode = () => {
    try {
      const raw = sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
      return window.GhaliaPromoConfig?.sanitizeStoredCode?.(raw) ?? raw;
    } catch (_) {
      return '';
    }
  };

  const persistCartPromoCode = (cart) => {
    const code = cartPromoCode(cart);
    if (!code) return;
    const sanitized = window.GhaliaPromoConfig?.sanitizeStoredCode?.(code) ?? code;
    if (!sanitized) return;
    try {
      sessionStorage.setItem(PROMO_STORAGE_KEY, sanitized);
    } catch (_) { /* noop */ }
  };

  const clearStoredPromoCode = () => {
    try {
      sessionStorage.removeItem(PROMO_STORAGE_KEY);
    } catch (_) { /* noop */ }
  };

  const applyCartPromoCode = async (code) =>
    GhaliaCartApi.updateCart({ discount: code }, null, {
      preserveDiscount: false,
      errorMessage: 'Could not update discount',
    });

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const withTimeout = (promise, ms, fallback = null) =>
    Promise.race([
      promise,
      new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
    ]);

  const CHECKOUT_PREP_TIMEOUT_MS = 1200;

  const cartPromoMeetsMinimum = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || !window.GhaliaPromo?.meetsMinimum) return true;
    const qualifyingNet = window.GhaliaPricing?.qualifyingSubtotalNet?.(cart, drawer) || 0;
    return window.GhaliaPromo.meetsMinimum(qualifyingNet, drawer);
  };

  const desiredPromoCode = () => {
    const code = storedPromoCode() || defaultPromoCode();
    return window.GhaliaPromoConfig?.sanitizeStoredCode?.(code) ?? code;
  };

  const cartHasShopifyPromoCode = (cart) => {
    if (cartPromoCode(cart)) return true;
    if (lineHasPromoCodeAllocation(cart)) return true;
    return codePromoAllocationNet(cart) > 0;
  };

  const persistDesiredPromoCode = (code) => {
    const sanitized = window.GhaliaPromoConfig?.sanitizeStoredCode?.(code) ?? code;
    if (!sanitized) return;
    try {
      sessionStorage.setItem(PROMO_STORAGE_KEY, sanitized);
    } catch (_) { /* noop */ }
  };

  const syncCartPromoEligibility = async (cart, options = {}) => {
    if (!cart || promoSyncBusy) return cart;
    promoSyncBusy = true;
    try {
      const meets = cartPromoMeetsMinimum(cart);
      const code = desiredPromoCode();

      if (!meets) {
        if (storedPromoCode()) clearStoredPromoCode();
        if (cartHasShopifyPromoCode(cart)) {
          try {
            return await applyCartPromoCode('');
          } catch (err) {
            console.warn('[GHALIA] Could not clear ineligible promo:', err);
            return cart;
          }
        }
        return cart;
      }

      if (!code) return cart;
      return ensureCartPromoCode(cart, options);
    } finally {
      promoSyncBusy = false;
    }
  };

  const defaultPromoCode = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    return window.GhaliaPromoConfig?.read?.(drawer)?.defaultCode || 'WELCOME';
  };

  let promoSyncBusy = false;

  const lineHasPromoCodeAllocation = (cart) =>
    (cart?.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some((alloc) =>
        isPromoCodeApplication(alloc?.discount_application)
      )
    );

  const promoAppliedOnShopify = (cart) =>
    Boolean(cartPromoCode(cart)) || lineHasPromoCodeAllocation(cart);

  const ensureCartPromoCode = async (cart, options = {}) => {
    const retries = Math.max(1, Number(options.retries) || 3);
    if (!cart) return cart;
    if (!cartPromoMeetsMinimum(cart)) return cart;
    persistCartPromoCode(cart);
    if (promoAppliedOnShopify(cart)) return cart;

    const code = desiredPromoCode();
    if (!code) return cart;
    persistDesiredPromoCode(code);

    let latest = cart;
    for (let attempt = 0; attempt < retries; attempt += 1) {
      try {
        const updated = await applyCartPromoCode(code);
        latest = updated;
        if (promoAppliedOnShopify(updated)) {
          persistDesiredPromoCode(code);
          if (window.GhaliaCart) window.GhaliaCart.lastCart = updated;
          repaintCartLinePromoPrices(updated);
          return updated;
        }
      } catch (err) {
        if (attempt === retries - 1) {
          console.warn('[GHALIA] Could not restore promo code:', err);
        }
      }
      if (attempt < retries - 1) {
        await sleep(250 * (attempt + 1));
        try {
          latest = await fetchCart();
        } catch (_) { /* noop */ }
      }
    }
    return latest;
  };

  const syncCartPromoUI = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const promo = drawer.querySelector('[data-cart-promo]');
    const form = drawer.querySelector('[data-promo-form]');
    const applied = drawer.querySelector('[data-cart-promo-applied]');
    const chip = drawer.querySelector('[data-cart-promo-chip]');
    const appliedCopy = drawer.querySelector('[data-cart-promo-applied-copy]');
    const input = drawer.querySelector('#cart-promo-code');
    const activeCode = cartPromoCode(cart);
    const shopifyPromo = promoAppliedOnShopify(cart);
    const storedCode = storedPromoCode();
    const displayCode = activeCode || (shopifyPromo && storedCode ? storedCode : '');
    if (input) input.value = displayCode;
    const showApplied = Boolean(displayCode) && shopifyPromo;
    if (showApplied) {
      const chipText = (activeCode || storedCode).toUpperCase();
      promo?.classList.add('is-applied');
      if (form) {
        form.hidden = true;
        form.setAttribute('hidden', '');
      }
      if (applied) {
        applied.hidden = false;
        applied.removeAttribute('hidden');
      }
      if (chip) chip.textContent = chipText;
      if (appliedCopy && chipText) {
        const template =
          window.Shopify?.strings?.cart?.promoAppliedHtml ||
          'Code {{code}} applied';
        appliedCopy.textContent = template.replace(/\{\{\s*code\s*\}\}/i, chipText);
      }
    } else {
      promo?.classList.remove('is-applied');
      if (form) {
        form.hidden = false;
        form.removeAttribute('hidden');
      }
      if (applied) {
        applied.hidden = true;
        applied.setAttribute('hidden', '');
      }
      if (chip) chip.textContent = '';
      if (appliedCopy) appliedCopy.textContent = '';
    }
    const drawerEl = document.querySelector('[data-cart-drawer]');
    if (drawerEl && window.GhaliaCartSummary?.apply) {
      window.GhaliaCartSummary.apply(cart, window.GhaliaCart?.lastStackState, drawerEl);
    }
  };

  let promoFastSyncRaf = null;
  let promoFastSyncCart = null;

  const scheduleImmediatePromoSync = (cart) => {
    if (!cart || promoSyncBusy || hasPendingCartMutations()) return;
    if (!promoNeedsRestore(cart) && cartHasShopifyPromoCode(cart)) return;
    if (!promoNeedsRestore(cart)) return;
    promoFastSyncCart = cart;
    if (promoFastSyncRaf) cancelAnimationFrame(promoFastSyncRaf);
    promoFastSyncRaf = requestAnimationFrame(() => {
      promoFastSyncRaf = null;
      const baseline = promoFastSyncCart;
      promoFastSyncCart = null;
      if (!baseline || promoSyncBusy || hasPendingCartMutations()) return;
      syncCartPromoEligibility(baseline, { retries: 3 })
        .then((promoCart) => {
          if (!promoCart || promoCart === baseline || hasPendingCartMutations()) return;
          const current = window.GhaliaCart?.lastCart;
          const baselineFp = cartMutationFingerprint(baseline);
          const currentFp = cartMutationFingerprint(current);
          if (current && currentFp !== baselineFp) return;
          if (cartItemTotal(promoCart) < cartItemTotal(current || baseline)) return;
          applyCartLineChangeFast(current || baseline, promoCart, { syncPromo: false });
          repaintCartLinePromoPrices(promoCart || baseline);
          scheduleCartPricingCoalesced(promoCart || baseline);
          if (!cartDrawerLinesMatchCart(promoCart || baseline)) {
            ensureCartLinesRendered(promoCart || baseline).catch(() => {});
          }
        })
        .catch(() => {});
    });
  };

  const FREE_GIFT_PROP = '_ghalia_free_gift';
  let freeGiftSyncQueue = Promise.resolve();
  let lastGiftDrawerPatchAt = 0;

  const markGiftDrawerPatched = () => {
    lastGiftDrawerPatchAt = Date.now();
  };

  const cartDrawerRenderedLineKeys = () => {
    if (!cartDrawer) return new Set();
    return new Set(
      [...cartDrawer.querySelectorAll('.cart-list [data-cart-line-key]')].map(
        (el) => el.dataset.cartLineKey
      )
    );
  };

  const cartDrawerLinesMatchCart = (cart) => {
    if (!cart?.items?.length) return !cartDrawerRenderedLineKeys().size;
    const domKeys = cartDrawerRenderedLineKeys();
    const cartKeys = (cart.items || []).map((item) => item.key);
    if (!domKeys.size || domKeys.size !== cartKeys.length) return false;
    return cartKeys.every((key) => domKeys.has(key));
  };

  const giftDrawerRecentlyPatched = () => Date.now() - lastGiftDrawerPatchAt < 4000;

  const canSkipDrawerRefresh = (cart) =>
    giftDrawerRecentlyPatched() && cartDrawerLinesMatchCart(cart);

  const ensureCartLinesRendered = async (cart) => {
    if (!cartDrawer || !cart?.items?.length) return;
    if (cartDrawerLinesMatchCart(cart)) return;
    await fetchCartDrawer();
    markGiftDrawerPatched();
  };

  const getCartGiftConfig = () => {
    if (!cartDrawer) return null;
    const variantId = cartDrawer.dataset.cartGiftVariantId;
    const threshold = parseInt(cartDrawer.dataset.cartGiftThreshold, 10);
    const productId = cartDrawer.dataset.cartGiftProductId;
    const handle = cartDrawer.dataset.cartGiftHandle || '';
    if (!variantId || !Number.isFinite(threshold)) return null;
    return {
      variantId: String(variantId),
      threshold: threshold < 0 ? 0 : threshold,
      productId: productId ? String(productId) : '',
      handle,
      alwaysGift: threshold <= 0,
    };
  };

  const isGiftCatalogLine = (item, config) => {
    if (!item || !config) return false;
    if (String(item.variant_id) === config.variantId) return true;
    if (config.productId && String(item.product_id) === config.productId) return true;
    if (config.handle && item.handle === config.handle) return true;
    return false;
  };

  const assertGiftAddAllowed = (variantId, properties, config = getCartGiftConfig()) => {
    if (!config || String(variantId) !== config.variantId) return;
    if (properties?.[FREE_GIFT_PROP] === 'true') return;
    throw new Error('This item is only available as a free gift on qualifying orders.');
  };

  const hasFreeGiftProperty = (item) => {
    if (!item?.properties) return false;
    const props = item.properties;
    if (props[FREE_GIFT_PROP] === 'true') return true;
    if (Array.isArray(props)) {
      return props.some((entry) => entry?.name === FREE_GIFT_PROP && entry?.value === 'true');
    }
    return false;
  };

  const linePropertiesSignature = (item) => {
    if (hasFreeGiftProperty(item)) return '';
    const props = item.properties;
    if (!props) return '';
    if (Array.isArray(props)) {
      const entries = props
        .filter((entry) => entry?.name && entry.name !== FREE_GIFT_PROP)
        .map((entry) => `${entry.name}=${entry.value}`)
        .sort();
      return entries.join('&');
    }
    return Object.keys(props)
      .filter((key) => key !== FREE_GIFT_PROP)
      .sort()
      .map((key) => `${key}=${props[key]}`)
      .join('&');
  };

  const findMergeableCartLine = (cart, variantId, properties, sellingPlan, config = getCartGiftConfig()) => {
    if (properties || sellingPlan) return null;
    return (cart?.items || []).find((item) => {
      if (String(item.variant_id) !== String(variantId)) return false;
      if (hasFreeGiftProperty(item)) return false;
      if (config && isGiftCatalogLine(item, config)) return false;
      if (item.selling_plan_allocation?.selling_plan?.id) return false;
      return !linePropertiesSignature(item);
    }) || null;
  };

  const consolidateDuplicatePaidLines = async (cart) => {
    if (!cart?.items?.length) return cart;
    const config = getCartGiftConfig();
    const groups = new Map();
    (cart.items || []).forEach((item) => {
      if (hasFreeGiftProperty(item)) return;
      if (config && isGiftCatalogLine(item, config)) return;
      const planId = item.selling_plan_allocation?.selling_plan?.id || '';
      const sig = `${item.variant_id}|${planId}|${linePropertiesSignature(item)}`;
      if (!groups.has(sig)) groups.set(sig, []);
      groups.get(sig).push(item);
    });

    const updates = {};
    groups.forEach((lines) => {
      if (lines.length <= 1) return;
      const totalQty = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
      updates[lines[0].key] = totalQty;
      for (let i = 1; i < lines.length; i += 1) {
        updates[lines[i].key] = 0;
      }
    });
    if (!Object.keys(updates).length) return cart;

    try {
      const updated = await GhaliaCartApi.updateCart({ updates }, cart, { softFail: true });
      return updated || cart;
    } catch (err) {
      console.warn('[GHALIA] Could not consolidate duplicate cart lines:', err);
      return cart;
    }
  };

  const isConfiguredFreeGiftLine = (item, config) => {
    if (!item || !config) return false;
    if (!hasFreeGiftProperty(item)) return false;
    if (String(item.variant_id) === config.variantId) return true;
    if (config.productId && String(item.product_id) === config.productId) return true;
    return false;
  };

  const findOrphanCatalogGiftLines = (cart, config) =>
    (cart?.items || []).filter(
      (item) => isGiftCatalogLine(item, config) && !hasFreeGiftProperty(item)
    );

  const isFreeGiftLine = (item, config) => {
    if (!item || !config) return false;
    if (isConfiguredFreeGiftLine(item, config)) return true;
    return hasFreeGiftProperty(item);
  };

  const findConfiguredFreeGiftLine = (cart, config) =>
    (cart?.items || []).find((item) => isConfiguredFreeGiftLine(item, config));

  const findWrongFreeGiftLines = (cart, config) =>
    (cart?.items || []).filter(
      (item) => hasFreeGiftProperty(item) && !isConfiguredFreeGiftLine(item, config)
    );

  const getQualifyingCartTotal = (cart, config) =>
    (cart?.items || []).reduce((sum, item) => {
      if (isGiftCatalogLine(item, config) || hasFreeGiftProperty(item)) return sum;
      return sum + (Number(item.final_line_price) || 0);
    }, 0);

  const giftSyncFingerprint = (cart, config) => {
    if (!cart?.items?.length || !config) return 'empty';
    const configured = findConfiguredFreeGiftLine(cart, config);
    const wrong = findWrongFreeGiftLines(cart, config);
    return [
      configured?.key || '',
      configured?.variant_id || '',
      ...wrong.map((item) => `${item.key}:${item.variant_id}`),
    ].join('|');
  };

  const giftNeedsAction = (cart, config) => {
    if (!cart || !config) return { shouldHave: false, hasGift: false, needsSync: false };
    const qualifying = getQualifyingCartTotal(cart, config);
    const hasPaid = (cart.items || []).some((item) => !isFreeGiftLine(item, config));
    const shouldHave = config.alwaysGift ? hasPaid : qualifying >= config.threshold;
    const hasGift = Boolean(findConfiguredFreeGiftLine(cart, config));
    return {
      shouldHave,
      hasGift,
      needsSync: (shouldHave && !hasGift) || (!shouldHave && hasGift),
    };
  };

  const giftNeedsNetworkSync = (cart, config) => {
    if (!cart || !config) return false;
    if (giftNeedsAction(cart, config).needsSync) return true;
    if (findWrongFreeGiftLines(cart, config).length) return true;
    const giftLine = findConfiguredFreeGiftLine(cart, config);
    if (giftLine && (Number(giftLine.quantity) || 0) > 1) return true;
    return (cart.items || []).some(
      (item) => isGiftCatalogLine(item, config) && !hasFreeGiftProperty(item)
    );
  };

  const mergeGiftAddIntoCart = (cart, addData, config) => {
    const line = addData?.items?.[0] || (addData?.key ? addData : null);
    if (!line?.key || !cart || !config) return null;
    const next = cloneCart(cart);
    next.items = (next.items || []).filter((item) => !isGiftCatalogLine(item, config));
    next.items.push(line);
    next.item_count = cartItemTotal(next);
    if (addData.item_count != null) next.item_count = addData.item_count;
    if (addData.total_price != null) {
      next.total_price = addData.total_price;
    } else {
      const addedLine = Number(line.final_line_price ?? line.line_price) || 0;
      next.total_price = (Number(next.total_price) || 0) + addedLine;
    }
    return next;
  };

  const syncCartFreeGift = async (cart) => {
    if (!cart) return cart;

    const run = async () => {
      const config = getCartGiftConfig();
      if (!config) return cart;
      if (!giftNeedsNetworkSync(cart, config)) return cart;

      const qualifying = getQualifyingCartTotal(cart, config);
      const hasPaidLines = (cart?.items || []).some(
        (item) => !isGiftCatalogLine(item, config) && !hasFreeGiftProperty(item)
      );
      const shouldHaveGift = config.alwaysGift ? hasPaidLines : qualifying >= config.threshold;
      const root = window.Shopify?.routes?.root || '/';

      try {
        const wrongGiftLines = findWrongFreeGiftLines(cart, config);
        const giftSkuUpdates = {};
        (cart?.items || []).forEach((item) => {
          if (!isGiftCatalogLine(item, config)) return;
          if (!hasFreeGiftProperty(item)) {
            giftSkuUpdates[item.key] = 0;
            return;
          }
          const qty = Number(item.quantity) || 0;
          if (qty > 1) giftSkuUpdates[item.key] = 1;
        });

        const updates = {};
        wrongGiftLines.forEach((item) => {
          updates[item.key] = 0;
        });
        Object.assign(updates, giftSkuUpdates);

        if (Object.keys(updates).length) {
          const beforeKeys = new Set((cart?.items || []).map((item) => item.key));
          cart = await GhaliaCartApi.updateCart({ updates }, cart);
          if (!cart) throw new Error('Could not sanitize free gift lines');
          const afterKeys = new Set((cart?.items || []).map((item) => item.key));
          let keysChanged = beforeKeys.size !== afterKeys.size;
          if (!keysChanged) {
            for (const key of beforeKeys) {
              if (!afterKeys.has(key)) {
                keysChanged = true;
                break;
              }
            }
          }
          if (cartDrawer && keysChanged) {
            await fetchCartDrawer();
            markGiftDrawerPatched();
          }
        }

        let giftLine = findConfiguredFreeGiftLine(cart, config);
        const promoToRestore =
          discountCodeToPreserve(cart) ||
          (cartPromoMeetsMinimum(cart) ? desiredPromoCode() : '');

        if (shouldHaveGift && !giftLine) {
          if (promoToRestore && !promoAppliedOnShopify(cart)) {
            try {
              cart = await applyCartPromoCode(promoToRestore);
            } catch (_) { /* noop */ }
          }
          const addBody = {
            id: config.variantId,
            quantity: 1,
            properties: { [FREE_GIFT_PROP]: 'true' },
            ...(cartDrawer
              ? {
                  sections: CART_DRAWER_SECTION,
                  sections_url: `${window.location.pathname}${window.location.search}`,
                }
              : {}),
          };
          let data;
          try {
            data = await GhaliaCartApi.addItem(addBody);
          } catch (firstErr) {
            await sleep(120);
            try {
              data = await GhaliaCartApi.addItem(addBody);
            } catch (secondErr) {
              const message = secondErr.message || 'Could not add free gift';
              showGhaliaToast(message);
              throw secondErr;
            }
          }
          if (applyCartDrawerHtml(data?.sections?.[CART_DRAWER_SECTION])) {
            markGiftDrawerPatched();
          }
          cart = mergeGiftAddIntoCart(cart, data, config) || (await fetchCart());
          if (promoToRestore && !promoAppliedOnShopify(cart)) {
            try {
              cart = await applyCartPromoCode(promoToRestore);
            } catch (_) { /* noop */ }
          }
        } else if (!shouldHaveGift && giftLine) {
          cart = await GhaliaCartApi.changeItem({ id: giftLine.key, quantity: 0 });
          if (!cart) throw new Error('Could not remove free gift');
          if (cartDrawer) {
            await fetchCartDrawer();
            markGiftDrawerPatched();
          }
        }
      } catch (err) {
        console.error('[GhaliaCartFreeGift]', err);
      }

      if (cart?.items?.length) {
        try {
          cart = (await consolidateDuplicatePaidLines(cart)) || cart;
        } catch (_) { /* noop */ }
      }
      return cart;
    };

    const next = freeGiftSyncQueue.then(run, run);
    freeGiftSyncQueue = next.catch(() => {});
    return next;
  };

  const PREVIEW_THEME_STORAGE_KEY = 'ghalia_preview_theme_id';

  const shopPreviewHosts = () => {
    const hosts = new Set([window.location.hostname]);
    const body = document.body;
    const permanent = body?.dataset?.shopPermanentDomain;
    const primary = body?.dataset?.shopPrimaryHost;
    if (permanent) hosts.add(permanent);
    if (primary) hosts.add(primary);
    if (window.Shopify?.shop) hosts.add(window.Shopify.shop);
    return hosts;
  };

  const isShopPreviewHost = (hostname) => {
    if (!hostname) return false;
    const hosts = shopPreviewHosts();
    if (hosts.has(hostname)) return true;
    return hostname.endsWith('.myshopify.com');
  };

  const persistPreviewThemeId = (previewId) => {
    if (!previewId) return;
    try {
      sessionStorage.setItem(PREVIEW_THEME_STORAGE_KEY, previewId);
    } catch (_) { /* noop */ }
  };

  const previewThemeId = () => {
    const fromQuery = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (fromQuery) {
      persistPreviewThemeId(fromQuery);
      return fromQuery;
    }
    try {
      const stored = sessionStorage.getItem(PREVIEW_THEME_STORAGE_KEY);
      if (stored) return stored;
    } catch (_) { /* noop */ }
    const fromTheme =
      window.GHALIA_PREVIEW_THEME_ID ||
      document.body?.dataset?.previewThemeId ||
      null;
    if (fromTheme) persistPreviewThemeId(fromTheme);
    return fromTheme;
  };

  const bindPreviewThemePersistence = () => {
    const url = new URL(window.location.href);
    let previewId = previewThemeId();

    if (!previewId && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        const fromRef = ref.searchParams.get('preview_theme_id');
        if (fromRef && isShopPreviewHost(ref.hostname) && isShopPreviewHost(url.hostname)) {
          previewId = fromRef;
          persistPreviewThemeId(fromRef);
        }
      } catch (_) { /* noop */ }
    }

    if (!previewId) return;

    if (!url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewId);
      window.history.replaceState({}, '', url.toString());
    }

    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      let link;
      try {
        link = new URL(anchor.href, window.location.origin);
      } catch (_) {
        return;
      }
      if (!isShopPreviewHost(link.hostname)) return;
      if (link.searchParams.has('preview_theme_id')) return;
      link.searchParams.set('preview_theme_id', previewId);
      anchor.href = link.toString();
    });
  };

  const applyCartDrawerHtml = (html) => {
    if (!html || !cartDrawer) return false;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const freshDrawer = tmp.querySelector('[data-cart-drawer]');
    const fresh = freshDrawer?.querySelector('[data-cart-drawer-body]') || tmp.querySelector('[data-cart-drawer-body]');
    const target = cartDrawer.querySelector('[data-cart-drawer-body]');
    if (!fresh || !target) return false;
    target.replaceWith(fresh);

    if (freshDrawer) {
      cartDrawer.dataset.cartGiftVariantId = freshDrawer.dataset.cartGiftVariantId || '';
      cartDrawer.dataset.cartGiftThreshold = freshDrawer.dataset.cartGiftThreshold || '';
      cartDrawer.dataset.cartGiftProductId = freshDrawer.dataset.cartGiftProductId || '';
      if (freshDrawer.dataset.cartGiftHandle) {
        cartDrawer.dataset.cartGiftHandle = freshDrawer.dataset.cartGiftHandle;
      }
      if (freshDrawer.dataset.cartGiftRetailNetCents) {
        cartDrawer.dataset.cartGiftRetailNetCents = freshDrawer.dataset.cartGiftRetailNetCents;
      }
    }

    const freshTitle = freshDrawer?.querySelector('.drawer__title');
    const targetTitle = cartDrawer.querySelector('.drawer__title');
    if (freshTitle && targetTitle) targetTitle.textContent = freshTitle.textContent;

    const freshFooter = freshDrawer?.querySelector('[data-cart-drawer-footer], .drawer__footer');
    const targetFooter = cartDrawer.querySelector('[data-cart-drawer-footer], .drawer__footer');
    if (freshFooter) {
      if (targetFooter) targetFooter.replaceWith(freshFooter);
      else cartDrawer.appendChild(freshFooter);
    }

    if (typeof initCartUpsellDrawer === 'function') initCartUpsellDrawer(cartDrawer);
    hydrateCartWrapAddForm();
    markGiftDrawerPatched();
    notifyCartDrawerRefreshed();
    return true;
  };

  const notifyCartDrawerRefreshed = () => {
    const cart = window.GhaliaCart?.lastCart;
    window.GhaliaCartStackDiscount?.resetFingerprint?.();
    runCartUiSync(cart);
    syncCartCheckoutState();
    scheduleDeferredCartSync({ cart, reason: 'drawer-refresh' });
    document.dispatchEvent(
      new CustomEvent('ghalia:cart-drawer:refreshed', { detail: { cart, coordinated: true } })
    );
  };

  const fetchCartDrawer = async () => {
    if (!cartDrawer) return false;
    const url = new URL(`${window.Shopify?.routes?.root || '/'}`, window.location.origin);
    url.searchParams.set('sections', CART_DRAWER_SECTION);
    const previewId = previewThemeId();
    if (previewId) url.searchParams.set('preview_theme_id', previewId);
    const res = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return false;
    let data;
    try {
      data = await res.json();
    } catch (_) {
      return false;
    }
    return applyCartDrawerHtml(data[CART_DRAWER_SECTION]);
  };

  const refreshCartAfterAdd = (cart, options = {}) => {
    if (!cart?.items?.length) return;
    if (options.drawerPatched) scheduleCartPricingCoalesced(cart);
  };

  const cartAddSectionsPayload = (minimal = false) =>
    !minimal && cartDrawer
      ? {
          sections: CART_DRAWER_SECTION,
          sections_url: `${window.location.pathname}${window.location.search}`,
        }
      : {};

  let cartAddQueue = Promise.resolve();

  const enqueueCartAdd = (fn) => {
    const job = cartAddQueue.then(() => fn());
    cartAddQueue = job.catch(() => {});
    return job;
  };

  const cartHasVariant = (cart, variantId) =>
    (cart?.items || []).some((item) => String(item.variant_id) === String(variantId));

  const applyCartBasicUpdate = (cart, beforeCart) => {
    if (!cart) return cart;
    if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
    renderCart(cart);
    updateDrawerTitle(cart);
    updateGiftBanner(patchCartGiftState(cart));
    syncCartPromoUI(cart);
    syncCartCheckoutState();
    scheduleCartPricingCoalesced(cart);
    if (cart?.items?.length) {
      scheduleDeferredCartSync({ beforeCart, cart, reason: 'add' });
    }
    return cart;
  };

  const cartBodyToFormFields = (body) => {
    const fields = {};
    if (Array.isArray(body.items) && body.items.length) {
      body.items.forEach((item, index) => {
        fields[`items[${index}][id]`] = String(item.id);
        fields[`items[${index}][quantity]`] = String(item.quantity ?? 1);
        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            fields[`items[${index}][properties][${key}]`] = String(value);
          });
        }
        if (item.selling_plan) fields[`items[${index}][selling_plan]`] = String(item.selling_plan);
      });
      return fields;
    }
    if (body.id != null) fields.id = String(body.id);
    if (body.quantity != null) fields.quantity = String(body.quantity);
    if (body.properties) {
      Object.entries(body.properties).forEach(([key, value]) => {
        fields[`properties[${key}]`] = String(value);
      });
    }
    if (body.selling_plan) fields.selling_plan = String(body.selling_plan);
    return fields;
  };

  const postCartFormIframe = (url, body) =>
    new Promise((resolve, reject) => {
      const fields = cartBodyToFormFields(body);
      if (!Object.keys(fields).length) {
        reject(new Error('Could not add to bag'));
        return;
      }
      const iframeName = `ghalia-cart-${Date.now()}`;
      const iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText =
        'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none';
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;
      form.target = iframeName;
      form.acceptCharset = 'UTF-8';
      form.style.display = 'none';
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.append(iframe, form);
      let settled = false;
      const cleanup = (res, isError) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        form.remove();
        iframe.remove();
        if (isError) reject(res);
        else resolve(res);
      };
      const timer = window.setTimeout(() => cleanup(new Error('Could not add to bag'), true), 12000);
      iframe.onload = () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          const text = (doc?.body?.textContent || '').trim();
          if (!text) {
            cleanup(new Error('Could not add to bag'), true);
            return;
          }
          const parsed = JSON.parse(text);
          const ok =
            (Array.isArray(parsed.items) && parsed.items.length > 0) ||
            Boolean(parsed.variant_id) ||
            (Boolean(parsed.id) && Boolean(parsed.quantity));
          if (!ok) {
            cleanup(new Error(parsed.description || parsed.message || 'Could not add to bag'), true);
            return;
          }
          cleanup(
            {
              ok: true,
              status: 200,
              json: async () => parsed,
              text: async () => text,
              clone() {
                return this;
              },
            },
            false
          );
        } catch (_) {
          cleanup(new Error('Could not add to bag'), true);
        }
      };
      form.submit();
    });

  const postCartJsonXhr = (url, payload) =>
    new Promise((resolve, reject) => {
      const xhr = new (NativeXHR())();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.withCredentials = true;
      xhr.onload = () => {
        const text = xhr.responseText || '';
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: async () => JSON.parse(text),
          text: async () => text,
          clone() {
            return this;
          },
        });
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(payload);
    });

  const postCartJson = async (url, body) => {
    if (cartApiIsBlocked()) {
      return {
        ok: false,
        status: 429,
        json: async () => ({ description: 'Too many requests' }),
        clone() {
          return this;
        },
      };
    }
    const payload = JSON.stringify(body);
    const transports = [
      () => postCartJsonXhr(url, payload),
      () =>
        getNativeFetch()(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: payload,
        }),
    ];
    let lastRes = null;
    for (let t = 0; t < transports.length; t += 1) {
      let res;
      try {
        res = await transports[t]();
      } catch (_) {
        continue;
      }
      if (res.status === 429) {
        blockCartApi();
        return res;
      }
      if (res.ok) return res;
      lastRes = res;
      if (res.status !== 422 && res.status < 500) return res;
    }
    return (
      lastRes || {
        ok: false,
        status: 0,
        json: async () => ({}),
        clone() {
          return this;
        },
      }
    );
  };

  const cartApiRoot = () => window.Shopify?.routes?.root || '/';

  const cartApiUrl = (path) => {
    const clean = String(path || '').replace(/^\//, '');
    return `${cartApiRoot()}${clean}`;
  };

  const cartApiParseError = async (res, fallback = 'Cart request failed') => {
    let message = fallback;
    try {
      const err = await res.json();
      message = err.description || err.message || message;
    } catch (_) { /* noop */ }
    const error = new Error(message);
    error.status = res.status;
    return error;
  };

  const cartApiParseJson = async (res) => {
    try {
      return await res.json();
    } catch (_) {
      return null;
    }
  };

  const cartApiSyncLastCart = (cart) => {
    if (cart && window.GhaliaCart) window.GhaliaCart.lastCart = cart;
    return cart;
  };

  const GhaliaCartApi = {
    isBlocked: () => cartApiIsBlocked(),

    async request(path, options = {}) {
      const method = (options.method || 'GET').toUpperCase();
      const url = /^https?:\/\//.test(path) ? path : cartApiUrl(path);

      if (method === 'GET') {
        const normalized = String(path || '').replace(/^\//, '');
        if (normalized === 'cart.js') {
          return fetchCart({ force: options.force });
        }
        if (cartApiIsBlocked()) throw new Error('Could not load bag');
        let res;
        try {
          res = await getNativeFetch()(url, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
        } catch (_) {
          res = await fetchCartJson(url);
        }
        if (res.status === 429) {
          blockCartApi();
          throw new Error('Could not load bag');
        }
        if (!res.ok) throw new Error('Request failed');
        return res.json();
      }

      const res = await postCartJson(url, options.body || {});
      if (options.rawResponse) return res;
      if (!res.ok) {
        if (options.softFail) return null;
        throw await cartApiParseError(res, options.errorMessage || 'Cart request failed');
      }
      return cartApiParseJson(res);
    },

    async getCart(options = {}) {
      const cart = await fetchCart({ force: options.force });
      return cartApiSyncLastCart(cart);
    },

    async addItem(body, options = {}) {
      const res = await postCartJson(`${cartApiRoot()}cart/add.js`, body || {});
      if (options.rawResponse) return res;
      if (!res.ok) {
        throw await cartApiParseError(res, options.errorMessage || 'Could not add to bag');
      }
      const data = await cartApiParseJson(res);
      if (data?.item_count != null) {
        cartApiSyncLastCart(data);
      } else if (options.syncCart !== false) {
        try {
          const cart = await fetchCart({ force: true });
          cartApiSyncLastCart(cart);
        } catch (_) { /* noop */ }
      }
      return data;
    },

    async changeItem(body, options = {}) {
      const res = await postCartJson(`${cartApiRoot()}cart/change.js`, body || {});
      if (!res.ok) {
        throw await cartApiParseError(res, options.errorMessage || 'Could not update bag');
      }
      variantStockCache.clear();
      return cartApiSyncLastCart(await cartApiParseJson(res));
    },

    async updateCart(body, cart = null, options = {}) {
      const baseCart = cart || window.GhaliaCart?.lastCart;
      let payload = { ...(body || {}) };
      if (options.preserveDiscount !== false && baseCart) {
        payload = withPreservedDiscount(payload, baseCart);
        const keepDiscount = cartPromoCode(baseCart) || desiredPromoCode();
        if (keepDiscount && !payload.discount && body?.updates) payload.discount = keepDiscount;
      }
      const res = await postCartJson(`${cartApiRoot()}cart/update.js`, payload);
      if (!res.ok) {
        if (options.softFail) return baseCart || null;
        throw await cartApiParseError(res, options.errorMessage || 'Could not update bag');
      }
      return cartApiSyncLastCart(await cartApiParseJson(res));
    },

    async clearCart(options = {}) {
      const res = await postCartJson(`${cartApiRoot()}cart/clear.js`, {});
      if (!res.ok) {
        throw await cartApiParseError(res, options.errorMessage || 'Could not clear bag');
      }
      return cartApiSyncLastCart(await cartApiParseJson(res));
    },

    refreshCartDrawer: () => fetchCartDrawer(),
  };

  window.GhaliaCartApi = GhaliaCartApi;

  window.GhaliaCartCoordinator = {
    uiSync: runCartUiSync,
    scheduleSync: scheduleDeferredCartSync,
    runBackgroundSync: runDeferredBackgroundSync,
  };

  const canSkipStockFromTrigger = (trigger) => {
    if (!trigger) return false;
    if (trigger.dataset?.variantAvailable === 'true') return true;
    if (trigger.closest?.('[data-pdp-tisento]') != null) return true;
    const form = trigger.closest('form[action*="/cart/add"]');
    const pdpSubmit = form?.querySelector('[data-product-submit]');
    if (pdpSubmit && !pdpSubmit.hidden && !pdpSubmit.disabled) return true;
    return false;
  };

  const processCartUpdate = async (cart, options = {}) => {
    const refresh = options.refresh || (options.fast ? 'light' : 'full');
    const giftConfig = getCartGiftConfig();
    const giftBefore = giftConfig ? giftSyncFingerprint(cart, giftConfig) : '';

    let synced = cart;
    if (!options.skipGiftSync && giftNeedsNetworkSync(cart, giftConfig)) {
      synced = await syncCartFreeGift(cart);
    }
    if (!options.skipConsolidate && synced?.items?.length > 1 && options.consolidate) {
      try {
        synced = (await consolidateDuplicatePaidLines(synced)) || synced;
      } catch (_) { /* noop */ }
    }

    const giftAfter = giftConfig ? giftSyncFingerprint(synced, giftConfig) : '';
    const giftAction = giftConfig ? giftNeedsAction(synced, giftConfig) : { needsSync: false };
    const giftMutated = giftBefore !== giftAfter || synced !== cart;
    const skipDrawer =
      options.skipDrawerRefresh || canSkipDrawerRefresh(synced);
    const shouldRefreshDrawer =
      !skipDrawer &&
      (refresh === 'full' ||
        giftMutated ||
        giftAction.needsSync ||
        !cartDrawerLinesMatchCart(synced));

    if (shouldRefreshDrawer) {
      suppressDrawerRefreshHandler = true;
      try {
        await fetchCartDrawer();
      } finally {
        suppressDrawerRefreshHandler = false;
      }
    } else {
      updateGiftBanner(synced);
      pruneRemovedCartLines(synced);
    }

    applyCartLocally(synced);
    updateDrawerTitle(synced);
    syncCartPromoUI(synced);
    if (
      refresh === 'full' &&
      (cartHasExtraWrap(synced) ||
        document.querySelector('[data-cart-gift-note]')?.value.trim() ||
        document.querySelector('[data-cart-wrap-note]')?.value.trim())
    ) {
      await saveCartOrderNote(synced);
    }
    return synced;
  };

  const pendingVariantAdds = new Map();

  const parseCartAddForm = (form) => {
    const fd = new FormData(form);
    const variantId = fd.get('id');
    const quantity = parseInt(fd.get('quantity') || '1', 10) || 1;
    const properties = {};
    fd.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = String(value);
    });
    const sellingPlan = fd.get('selling_plan');
    return {
      variantId,
      quantity,
      properties: Object.keys(properties).length ? properties : undefined,
      sellingPlan: sellingPlan ? String(sellingPlan) : null,
    };
  };

  const addVariantToCart = async (variantId, trigger, options = {}) => {
    const {
      quantity = 1,
      openCart = true,
      skipStockCheck = false,
      properties,
      sellingPlan,
    } = options;
    if (!variantId) throw new Error('Missing variant');
    assertGiftAddAllowed(variantId, properties);
    const addKey = String(variantId);
    if (pendingVariantAdds.has(addKey)) return pendingVariantAdds.get(addKey);

    const task = enqueueCartAdd(async () => {
      cartAddsInFlight += 1;
      const maxItems = getCartMaxItems();
      const beforeCart = window.GhaliaCart?.lastCart || null;
      const openingEmpty = !beforeCart?.items?.length;
      const openDrawerAfter =
        openCart &&
        cartDrawer &&
        cartDrawer.getAttribute(CART_DISMISS_DURING_ADD) !== 'true';
      if (maxItems > 0 && cartItemTotal(beforeCart) + quantity > maxItems) {
        showGhaliaToast(`Bag limit is ${maxItems} items`);
        throw new Error(`Bag limit is ${maxItems} items`);
      }
      const existingLine = findMergeableCartLine(beforeCart, variantId, properties, sellingPlan);
      const targetQty = existingLine
        ? (Number(existingLine.quantity) || 0) + quantity
        : quantity;
      trigger?.setAttribute('aria-busy', 'true');
      cartDrawer?.removeAttribute(CART_DISMISS_DURING_ADD);
      try {
        if (openCart && cartDrawer && !openingEmpty) openDrawer(cartDrawer);
        if (existingLine) applyOptimisticLineQty(existingLine.key, targetQty);
        const id = normalizeVariantId(variantId);
        let addData;
        if (existingLine) {
          addData = await GhaliaCartApi.changeItem({
            id: existingLine.key,
            quantity: targetQty,
          });
        } else {
          const item = { id, quantity };
          if (properties && Object.keys(properties).length) item.properties = properties;
          if (sellingPlan) item.selling_plan = sellingPlan;
          try {
            addData = await GhaliaCartApi.addItem({ items: [item] }, { syncCart: false });
          } catch (firstErr) {
            try {
              addData = await GhaliaCartApi.addItem(
                {
                  id,
                  quantity,
                  ...(properties && Object.keys(properties).length ? { properties } : {}),
                  ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
                },
                { syncCart: false }
              );
            } catch (secondErr) {
              const synced = await GhaliaCartApi.getCart().catch(() => null);
              if (cartHasVariant(synced, variantId)) {
                applyCartBasicUpdate(synced, beforeCart);
                if (openDrawerAfter) openDrawer(cartDrawer);
                return synced;
              }
              throw secondErr;
            }
          }
        }
        variantStockCache.clear();
        let cart = addData?.items ? addData : null;
        try {
          cart = await GhaliaCartApi.getCart();
        } catch (_) { /* noop */ }
        if (!cartHasVariant(cart, variantId)) {
          const line = addData?.items?.[0] || (addData?.variant_id ? addData : null);
          if (line?.variant_id || line?.id) {
            cart = {
              ...(beforeCart || { items: [], item_count: 0, total_price: 0 }),
              items: [line],
              item_count: Number(line.quantity) || quantity,
            };
          }
        }
        applyCartBasicUpdate(cart, beforeCart);
        refreshCartAfterAdd(cart);
        if (openDrawerAfter) openDrawer(cartDrawer);
        document.dispatchEvent(
          new CustomEvent('ghalia:cart:add-variant:done', {
            detail: { variantId, quantity, cart },
          })
        );
        return cart;
      } catch (err) {
        try {
          const synced = await GhaliaCartApi.getCart();
          if (cartHasVariant(synced, variantId)) {
            applyCartBasicUpdate(synced, beforeCart);
            if (openDrawerAfter) openDrawer(cartDrawer);
            return synced;
          }
        } catch (_) {
          if (beforeCart) applyCartLocally(beforeCart, { promoUi: false });
        }
        showGhaliaToast(err.message || 'Could not add to bag');
        err._ghaliaCartToast = true;
        document.dispatchEvent(
          new CustomEvent('ghalia:cart:add-variant:error', { detail: { variantId, error: err } })
        );
        throw err;
      } finally {
        trigger?.removeAttribute('aria-busy');
        cartDrawer?.removeAttribute(CART_DISMISS_DURING_ADD);
        cartAddsInFlight = Math.max(0, cartAddsInFlight - 1);
      }
    });

    pendingVariantAdds.set(addKey, task);
    try {
      return await task;
    } finally {
      if (pendingVariantAdds.get(addKey) === task) pendingVariantAdds.delete(addKey);
    }
  };

  document.addEventListener('ghalia:cart:add-variant', async (e) => {
    const { variantId, trigger, quantity = 1, openCart = true } = e.detail || {};
    if (!variantId) return;
    try {
      await addVariantToCart(variantId, trigger, { quantity, openCart });
    } catch (err) {
      console.error(err);
    }
  });

  const addItemsToCart = async (items, trigger, options = {}) => {
    const { openCart = true } = options;
    const payload = (items || []).filter((item) => item?.id).map((item) => {
      const row = {
        id: Number(item.id),
        quantity: Number(item.quantity || 1),
      };
      if (item.properties && Object.keys(item.properties).length) {
        row.properties = item.properties;
      }
      return row;
    });
    if (!payload.length) throw new Error('Missing variant');
    payload.forEach((row) => assertGiftAddAllowed(row.id, row.properties));
    return runCartMutation(async () => {
      cartAddsInFlight += 1;
      trigger?.setAttribute('aria-busy', 'true');
      const openDrawerAfter = openCart && cartDrawer;
      try {
        const beforeCart = window.GhaliaCart?.lastCart;
        const openingEmpty = !beforeCart?.items?.length;
        if (openCart && cartDrawer && !openingEmpty) openDrawer(cartDrawer);
        const skipCheck =
          options.skipStockCheck ||
          canSkipStockFromTrigger(trigger) ||
          trigger?.closest?.('[data-pdp-tisento]') != null;
        if (!skipCheck) {
          for (const row of payload) {
            await assertVariantCanAdd(row.id, row.quantity);
          }
        }
        const res = await GhaliaCartApi.addItem(
          { items: payload, ...cartAddSectionsPayload() },
          { rawResponse: true }
        );
        if (!res.ok) {
          let message = 'Could not add to bag';
          try {
            const err = await res.json();
            message = err.description || err.message || message;
          } catch (_) { /* noop */ }
          throw new Error(message);
        }
        variantStockCache.clear();
        const addData = await res.json();
        const drawerPatched = applyCartDrawerHtml(addData?.sections?.[CART_DRAWER_SECTION]);
        const cart = await GhaliaCartApi.getCart();
        const synced = finishCartClientRefresh(cart, beforeCart);
        refreshCartAfterAdd(synced, { drawerPatched });
        document.dispatchEvent(
          new CustomEvent('ghalia:cart:add-items:done', { detail: { items: payload, cart: synced } })
        );
        return synced;
      } catch (err) {
        showGhaliaToast(err.message || 'Could not add to bag');
        document.dispatchEvent(
          new CustomEvent('ghalia:cart:add-items:error', { detail: { items: payload, error: err } })
        );
        throw err;
      } finally {
        trigger?.removeAttribute('aria-busy');
        finishCartAddMutation({ openDrawerAfter, cartDrawerEl: cartDrawer });
      }
    });
  };

  const upgradeToBundle = async (trigger, { updates, variantId, quantity = 1, openCart = true }) =>
    runCartMutation(async () => {
      cartAddsInFlight += 1;
      if (!variantId) throw new Error('Missing bundle variant');
      trigger?.setAttribute('aria-busy', 'true');
      cartDrawer?.removeAttribute(CART_DISMISS_DURING_ADD);
      const openDrawerAfter = openCart && cartDrawer;
      try {
        if (updates && Object.keys(updates).length) {
          await GhaliaCartApi.updateCart({ updates }, null, {
            preserveDiscount: false,
            errorMessage: 'Could not update bag',
          });
        }
        const beforeCartForOpen = window.GhaliaCart?.lastCart;
        const openingEmpty = !beforeCartForOpen?.items?.length;
        if (openCart && cartDrawer && !openingEmpty) openDrawer(cartDrawer);
        const payload = { id: variantId, quantity, ...cartAddSectionsPayload() };
        const res = await GhaliaCartApi.addItem(payload, { rawResponse: true });
        if (!res.ok) {
          let message = 'Could not add bundle';
          try {
            const err = await res.json();
            message = err.description || err.message || message;
          } catch (_) { /* noop */ }
          throw new Error(message);
        }
        variantStockCache.clear();
        const addData = await res.json();
        const drawerPatched = applyCartDrawerHtml(addData?.sections?.[CART_DRAWER_SECTION]);
        const beforeCart = window.GhaliaCart?.lastCart;
        const cart = await GhaliaCartApi.getCart();
        const synced = finishCartClientRefresh(cart, beforeCart);
        refreshCartAfterAdd(synced, { drawerPatched });
        document.dispatchEvent(
          new CustomEvent('ghalia:cart:bundle-upgrade:done', {
            detail: { variantId, quantity, cart: synced },
          })
        );
        return synced;
      } finally {
        trigger?.removeAttribute('aria-busy');
        cartDrawer?.removeAttribute(CART_DISMISS_DURING_ADD);
        finishCartAddMutation({ openDrawerAfter, cartDrawerEl: cartDrawer });
      }
    });

  window.GhaliaCart = {
    addVariant: (variantId, opts = {}) => addVariantToCart(variantId, null, opts),
    addItems: addItemsToCart,
    upgradeToBundle,
    lastCart: null,
    refresh: async () => processCartUpdate(await fetchCart()),
    getCart: (options) => GhaliaCartApi.getCart(options),
    addItem: (body, options) => GhaliaCartApi.addItem(body, options),
    changeItem: (body, options) => GhaliaCartApi.changeItem(body, options),
    updateCart: (body, cart, options) => GhaliaCartApi.updateCart(body, cart, options),
    clearCart: (options) => GhaliaCartApi.clearCart(options),
    refreshCartDrawer: () => GhaliaCartApi.refreshCartDrawer(),
    request: (path, options) => GhaliaCartApi.request(path, options),
  };

  /* Cart gift sections — sync gift message + extra wrap to order notes */
  let giftNoteTimer = null;
  let giftNoteSaving = false;

  const getCartWrapVariantId = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const el =
      drawer?.querySelector('[data-cart-wrap-variant-id]') ||
      drawer?.querySelector('[data-cart-wrap-variant-input]') ||
      document.querySelector('[data-cart-wrap-variant-id]');
    return parseInt(el?.dataset?.cartWrapVariantId || el?.value || '', 10) || 0;
  };

  const hydrateCartWrapAddForm = async () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const form = drawer.querySelector('[data-cart-wrap-add-form]');
    const input = form?.querySelector('[data-cart-wrap-variant-input]');
    if (input?.value) return;
    const handle = drawer.dataset.cartWrapHandle;
    const sku = drawer.dataset.cartWrapSku || '';
    if (!handle) return;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const product = await res.json();
      const variant =
        (product?.variants || []).find((v) => sku && v.sku === sku) ||
        (product?.variants || []).find((v) => v.available) ||
        product?.variants?.[0];
      if (!variant?.id) return;
      if (!form) {
        const panel = drawer.querySelector('.cart-wrap--extra .cart-wrap__form');
        if (!panel) return;
        const wrapForm = document.createElement('form');
        wrapForm.className = 'cart-wrap__add-form';
        wrapForm.setAttribute('action', `${window.Shopify?.routes?.root || '/'}cart/add`);
        wrapForm.setAttribute('method', 'post');
        wrapForm.dataset.cartWrapAddForm = '';
        wrapForm.innerHTML = `<input type="hidden" name="id" value="${variant.id}" data-cart-wrap-variant-input><button class="btn btn--primary btn--block cart-wrap__add" type="submit" data-cart-wrap-add-btn>Add to Bag</button>`;
        panel.appendChild(wrapForm);
      } else if (input) {
        input.value = String(variant.id);
      }
      drawer.dataset.cartWrapVariantId = String(variant.id);
    } catch (_) { /* noop */ }
  };

  const cartHasExtraWrap = (cart) => {
    const wrapId = getCartWrapVariantId();
    if (!wrapId || !cart?.items?.length) return false;
    return cart.items.some((item) => item.variant_id === wrapId);
  };

  const getOrderNoteLabels = () => {
    const root = document.querySelector('[data-cart-order-notes]');
    const fallback = {
      gift: 'FREE — Gift card message (print on card):',
      wrap: 'PAID ADD-ON — Extra gift wrap note:',
      wrapAdded: 'Extra wrap product in cart',
    };
    if (!root) return fallback;
    return {
      gift: root.dataset.orderNoteGiftPrefix || fallback.gift,
      wrap: root.dataset.orderNoteWrapPrefix || fallback.wrap,
      wrapAdded: root.dataset.orderNoteWrapAdded || fallback.wrapAdded,
    };
  };

  const buildCartOrderNote = (giftMessage, wrapMessage, hasExtraWrap, labels) => {
    const parts = [];
    if (giftMessage) parts.push(`${labels.gift}\n${giftMessage}`);
    const wrapLines = [];
    if (hasExtraWrap) wrapLines.push(labels.wrapAdded);
    if (wrapMessage) wrapLines.push(wrapMessage);
    if (wrapLines.length) parts.push(`${labels.wrap}\n${wrapLines.join('\n')}`);
    return parts.join('\n\n');
  };

  const updateGiftNoteCount = (textarea) => {
    const isWrap = textarea.matches('[data-cart-wrap-note]');
    const countSel = isWrap ? '[data-cart-wrap-note-count]' : '[data-cart-gift-note-count]';
    const countEl = textarea.closest('.cart-wrap__panel')?.querySelector(countSel);
    if (!countEl) return;
    const max = parseInt(textarea.dataset.cartGiftNoteMax || '100', 10);
    const remaining = Math.max(0, max - textarea.value.length);
    const template = countEl.dataset.countTemplate || '';
    countEl.textContent = template.replace(/__COUNT__/g, String(remaining));
  };

  const saveCartOrderNote = async (cart) => {
    if (giftNoteSaving) return;
    const labels = getOrderNoteLabels();
    const giftMessage = document.querySelector('[data-cart-gift-note]')?.value.trim() || '';
    const wrapMessage = document.querySelector('[data-cart-wrap-note]')?.value.trim() || '';
    let activeCart = cart;
    if (!activeCart) {
      try {
        activeCart = await fetchCart();
      } catch (err) {
        console.error(err);
        return;
      }
    }
    const note = buildCartOrderNote(giftMessage, wrapMessage, cartHasExtraWrap(activeCart), labels);
    giftNoteSaving = true;
    try {
      await GhaliaCartApi.updateCart({ note }, activeCart, { preserveDiscount: false });
    } catch (err) {
      console.error(err);
    } finally {
      giftNoteSaving = false;
    }
  };

  document.addEventListener('input', (e) => {
    const textarea = e.target.closest('[data-cart-gift-note], [data-cart-wrap-note]');
    if (!textarea) return;
    updateGiftNoteCount(textarea);
    clearTimeout(giftNoteTimer);
    giftNoteTimer = window.setTimeout(() => saveCartOrderNote(), 500);
  });

  document.addEventListener(
    'blur',
    (e) => {
      const textarea = e.target.closest('[data-cart-gift-note], [data-cart-wrap-note]');
      if (!textarea) return;
      clearTimeout(giftNoteTimer);
      saveCartOrderNote();
    },
    true
  );

  const updateCartDiscount = async (code, trigger) => {
    trigger?.setAttribute('aria-busy', 'true');
    try {
      if (code) {
        try {
          sessionStorage.setItem(PROMO_STORAGE_KEY, code);
        } catch (_) { /* noop */ }
      } else {
        clearStoredPromoCode();
      }
      const cart = await applyCartPromoCode(code);
      await processCartUpdate(cart, { syncPromo: true });
      syncCartPromoUI(cart);
      if (cartDrawer) openDrawer(cartDrawer);
    } catch (err) {
      console.error(err);
      showGhaliaToast(err.message || 'Could not update discount');
    } finally {
      trigger?.removeAttribute('aria-busy');
    }
  };

  const scheduleBackgroundPromoSync = () => {};

  document.addEventListener('ghalia:cart:stack-discount', (e) => {
    const cart = e.detail?.cart || window.GhaliaCart?.lastCart;
    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && window.GhaliaCartSummary?.apply) {
      window.GhaliaCartSummary.apply(cart, e.detail?.state, drawer);
    }
  });

  document.addEventListener('ghalia:cart-drawer:refreshed', () => {
    if (suppressDrawerRefreshHandler) {
      syncCartCheckoutState();
      return;
    }
    syncCartCheckoutState();
  });

  document.addEventListener('ghalia:drawer:open', (e) => {
    const drawer = e.detail?.drawer;
    if (!drawer?.hasAttribute('data-cart-drawer')) return;
    if (!e.detail?.preserveQuickAdd) {
      window.GhaliaCartQuickAdd?.close?.();
      drawer.classList.remove('is-quick-add-open');
    }
    const syncDrawerOnOpen = async () => {
      let cart = window.GhaliaCart?.lastCart;
      if (!cart?.items?.length) {
        try {
          cart = await GhaliaCartApi.getCart({ force: true });
        } catch (_) {
          cart = null;
        }
      }
      if (!cart?.items?.length) {
        syncCartPromoUI(cart);
        syncCartCheckoutState();
        return;
      }
      applyCartLocally(cart);
      updateDrawerTitle(cart);
      syncCartPromoUI(cart);
      syncCartCheckoutState();
      if (!cartDrawerLinesMatchCart(cart)) {
        await ensureCartLinesRendered(cart);
        const fresh = window.GhaliaCart?.lastCart || cart;
        applyCartLocally(fresh);
        updateDrawerTitle(fresh);
        syncCartPromoUI(fresh);
        syncCartCheckoutState();
      }
    };
    syncDrawerOnOpen().catch((err) => console.error(err));
  });

  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-promo-remove]');
    if (!removeBtn) return;
    e.preventDefault();
    await updateCartDiscount('', removeBtn);
  });

  document.addEventListener('submit', async (e) => {
    const promoForm = e.target.closest('[data-promo-form]');
    if (!promoForm) return;
    e.preventDefault();
    const input = promoForm.querySelector('[name="discount"]');
    const code = input?.value.trim();
    if (!code) {
      input?.focus();
      return;
    }
    const btn = promoForm.querySelector('[type="submit"]');
    await updateCartDiscount(code, btn);
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const { variantId, quantity, properties, sellingPlan } = parseCartAddForm(form);
    if (!variantId) {
      showGhaliaToast(window.Shopify?.strings?.cart?.selectOption || 'Please select an option');
      return;
    }
    try {
      const synced = await addVariantToCart(variantId, btn, {
        quantity,
        openCart: true,
        properties,
        sellingPlan,
      });
      if (form.classList.contains('cart-wrap__add-form')) {
        await saveCartOrderNote(synced);
      }
    } catch (err) {
      console.error(err);
    }
  });

  window.GhaliaCartQuickAddDeps = { addVariantToCart, openDrawer, cartDrawer };
  window.GhaliaCartQuickAdd?.init(window.GhaliaCartQuickAddDeps);

  /* ---------- Wishlist (Swym sync + local UI) ---------- */
  const WISHLIST_KEY = 'ghalia-wishlist';
  const wishlistStrings = window.Shopify?.strings || {};

  const wishlistItemKey = (productId, variantId) => `${productId}:${variantId || ''}`;

  const resolveWishlistHandle = (item) => {
    if (item?.productHandle) return String(item.productHandle).trim();
    const url = item?.productUrl || '';
    if (!url) return '';
    try {
      const path = url.startsWith('http') ? new URL(url, window.location.origin).pathname : url;
      const match = path.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const isValidWishlistItem = (item) => {
    const productId = String(item?.productId || '').trim();
    return !!productId && !!resolveWishlistHandle(item);
  };

  const normalizeWishlistItems = (items) => {
    const seen = new Set();
    const out = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!isValidWishlistItem(item)) return;
      const productId = String(item.productId).trim();
      const variantId = String(item?.variantId || '');
      const key = wishlistItemKey(productId, variantId);
      if (seen.has(key)) return;
      seen.add(key);
      const handle = resolveWishlistHandle(item);
      out.push({
        ...item,
        productId,
        variantId,
        productHandle: item.productHandle || handle
      });
    });
    return out;
  };

  const getWishlistBadgeCount = (items) => {
    const productIds = new Set();
    normalizeWishlistItems(items).forEach((item) => productIds.add(String(item.productId)));
    return productIds.size;
  };

  const readLocalWishlist = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      const normalized = normalizeWishlistItems(raw);
      if (normalized.length !== raw.length) {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(normalized));
        document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
      }
      return normalized;
    } catch (_) {
      localStorage.removeItem(WISHLIST_KEY);
      return [];
    }
  };

  const writeLocalWishlist = (items) => {
    const normalized = normalizeWishlistItems(items);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(normalized));
    updateWishlistCount();
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:update'));
  };

  const showWishlistToast = (message) => showGhaliaToast(message);

  let wishlistBadgeSyncing = false;

  const scrubWishlistHeaderBtn = (count) => {
    const btn = document.querySelector('[data-wishlist-header-btn]');
    if (!btn) return;
    const total = normalizeHeaderCount(count);
    btn.querySelectorAll(':scope > *:not(svg.icon)').forEach((el) => {
      if (total === 0 || !el.hasAttribute('data-wishlist-count')) el.remove();
    });
    if (total === 0) btn.querySelectorAll('[data-wishlist-count], .site-actions__count').forEach((el) => el.remove());
  };

  const updateWishlistCount = () => {
    if (wishlistBadgeSyncing) return;
    wishlistBadgeSyncing = true;
    const items = readLocalWishlist();
    const count = getWishlistBadgeCount(items);
    try {
      scrubWishlistHeaderBtn(count);
      syncHeaderCountBadge({
        parentSelector: '[data-wishlist-header-btn]',
        badgeAttr: 'data-wishlist-count',
        count,
      });
      syncSwymWishlistCounter(0);
    } finally {
      wishlistBadgeSyncing = false;
    }
  };

  const setWishlistBtnState = (btn, active) => {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    const savedLabel = btn.dataset.wishlistLabelSaved || '';
    const addLabel = btn.dataset.wishlistLabelAdd || btn.getAttribute('aria-label') || '';
    if (savedLabel) btn.setAttribute('aria-label', active ? savedLabel : addLabel);
  };

  const syncWishlistButtons = (updateHeader = true) => {
    const local = new Set(readLocalWishlist().map((item) => wishlistItemKey(item.productId, item.variantId)));
    $$('[data-wishlist-toggle]').forEach((btn) => {
      const productId = btn.getAttribute('data-wishlist-toggle') || '';
      const variantId = btn.getAttribute('data-variant-id') || '';
      const active = local.has(wishlistItemKey(productId, variantId))
        || [...local].some((key) => key.split(':')[0] === productId);
      setWishlistBtnState(btn, active);
    });
    if (updateHeader) updateWishlistCount();
  };

  const swymPayloadFor = ({ productId, variantId, productUrl }) => ({
    empi: parseInt(productId, 10),
    epi: parseInt(variantId, 10),
    du: productUrl
  });

  const syncToSwym = (payload, action) => {
    const swymPayload = swymPayloadFor(payload);
    const run = (swat) => {
      if (action === 'add' && swat?.addToWishList) {
        swat.addToWishList(swymPayload, () => {}, () => {});
      } else if (action === 'remove' && swat?.removeFromWishList) {
        swat.removeFromWishList(swymPayload, () => {}, () => {});
      }
    };
    if (window._swat) run(window._swat);
    else {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(run);
    }
  };

  const addToWishlist = (payload, btn) => {
    const { productId, variantId } = payload;
    const items = readLocalWishlist();
    const key = wishlistItemKey(productId, variantId);
    if (!items.some((item) => wishlistItemKey(item.productId, item.variantId) === key)) {
      items.push(payload);
      writeLocalWishlist(items);
    }
    if (btn) setWishlistBtnState(btn, true);
    showWishlistToast(wishlistStrings.wishlistAdded || 'Saved to wishlist');
    syncToSwym(payload, 'add');
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:add', { detail: payload }));
  };

  const removeFromWishlist = (payload, btn) => {
    const { productId, variantId } = payload;
    const items = readLocalWishlist().filter((item) => wishlistItemKey(item.productId, item.variantId) !== wishlistItemKey(productId, variantId));
    writeLocalWishlist(items);
    if (btn) setWishlistBtnState(btn, false);
    showWishlistToast(wishlistStrings.wishlistRemoved || 'Removed from wishlist');
    syncToSwym(payload, 'remove');
    document.dispatchEvent(new CustomEvent('ghalia:wishlist:remove', { detail: payload }));
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const productId = btn.getAttribute('data-wishlist-toggle') || '';
    const variantId = btn.getAttribute('data-variant-id') || btn.closest('[data-product-card]')?.querySelector('[data-quick-add]')?.getAttribute('data-quick-add') || '';
    const productUrl = btn.getAttribute('data-product-url') || btn.closest('[data-product-card]')?.querySelector('[data-product-url]')?.getAttribute('href') || window.location.pathname;
    const productHandle = btn.getAttribute('data-product-handle') || btn.closest('[data-product-card]')?.querySelector('[data-wishlist-toggle]')?.getAttribute('data-product-handle') || '';
    if (!productId) return;

    const payload = { productId, variantId, productUrl, productHandle };

    if (btn.classList.contains('is-active')) {
      removeFromWishlist(payload, btn);
      return;
    }

    addToWishlist(payload, btn);
  });

  syncWishlistButtons();
  document.addEventListener('ghalia:wishlist:sync-buttons', (e) => {
    const updateHeader = e.detail?.header !== false;
    syncWishlistButtons(updateHeader);
  });

  const hideInjectedSwymCounters = () => {
    document
      .querySelectorAll(
        '.swym-wishlist-counter, [class*="swym"][class*="count"], [data-wishlist-header-btn] [class*="swym"]'
      )
      .forEach((node) => {
        if (node.matches('[data-wishlist-count]')) return;
        node.hidden = true;
        node.style.display = 'none';
        node.setAttribute('aria-hidden', 'true');
      });
  };

  /* One-shot Swym cleanup — no MutationObservers (they froze the site) */
  ensureWishlistHeaderOpensDrawer();
  hideInjectedSwymCounters();
  updateWishlistCount();
  window.setTimeout(() => {
    ensureWishlistHeaderOpensDrawer();
    hideInjectedSwymCounters();
    updateWishlistCount();
  }, 1500);
  window.setTimeout(() => {
    ensureWishlistHeaderOpensDrawer();
    hideInjectedSwymCounters();
    updateWishlistCount();
  }, 4000);

  if (document.body.classList.contains('template-page-wishlist')
    || document.body.classList.contains('template-page-swym')
    || document.body.classList.contains('template-page-swym-wishlist')) {
    window.addEventListener('load', () => window.setTimeout(openWishlistDrawer, 150));
  }

  window.GhaliaWishlist = {
    read: readLocalWishlist,
    write: writeLocalWishlist,
    getCount: () => getWishlistBadgeCount(readLocalWishlist()),
    remove: (payload) => removeFromWishlist(payload, null)
  };

  fetchCart()
    .then(async (cart) => {
      if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
      renderCart(cart);
      updateDrawerTitle(cart);
      if (!cart?.items?.length) return;
      scheduleCartPricing(cart);
    })
    .catch(() => renderCart({ item_count: 0, items: [] }));

  window.addEventListener('pageshow', (e) => {
    syncCartCheckoutState();
    if (!e.persisted) return;
    GhaliaCartApi.getCart({ force: true })
      .then((cart) => {
        if (window.GhaliaCart) window.GhaliaCart.lastCart = cart;
        renderCart(cart);
        updateDrawerTitle(cart);
        if (!cart?.items?.length) {
          showCartDrawerEmpty();
          return;
        }
        if (cartDrawer?.dataset.open === 'true' && !cartDrawerLinesMatchCart(cart)) {
          ensureCartLinesRendered(cart).catch(() => {});
        }
      })
      .catch(() => {});
  });

  /* Change line item quantity / remove */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-line-qty]');
    if (!input || input.disabled) return;
    e.stopPropagation();
    const key = input.dataset.cartLineQty;
    const quantity = parseInt(input.value, 10) || 0;
    queueCartLineChange(key, quantity);
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-cart-line-remove]');
    if (!rm || rm.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    removeCartLineNow(rm.dataset.cartLineRemove);
  });

  const customerEnteredPromoCode = () => Boolean(storedPromoCode());

  const prepareCartForCheckout = async (cart) => {
    if (!cart) return cart;
    let latest = cart;
    const config = getCartGiftConfig();

    if (latest?.items?.length > 1 && hasDuplicatePaidCartLines(latest)) {
      latest = (await consolidateDuplicatePaidLines(latest)) || latest;
    }

    if (config && giftNeedsNetworkSync(latest, config)) {
      latest = (await syncCartFreeGift(latest)) || latest;
    }

    if (window.GhaliaQualifyingBasket?.syncAttribute) {
      const drawer = document.querySelector('[data-cart-drawer]');
      latest = (await window.GhaliaQualifyingBasket.syncAttribute(latest, drawer)) || latest;
    }

    if (customerEnteredPromoCode()) {
      if (!cartPromoMeetsMinimum(latest) && cartHasShopifyPromoCode(latest)) {
        try {
          latest = await applyCartPromoCode('');
        } catch (_) { /* noop */ }
      } else if (cartPromoMeetsMinimum(latest) && !promoAppliedOnShopify(latest)) {
        latest = (await ensureCartPromoCode(latest, { retries: 1 })) || latest;
      }
    }

    if (window.GhaliaCart) window.GhaliaCart.lastCart = latest;
    return latest;
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form');
    const checkoutBtn = form?.querySelector('button[name="checkout"]');
    if (!checkoutBtn) return;
    if (document.querySelector('[data-cart-line-available="false"]')) {
      e.preventDefault();
      showGhaliaToast(window.Shopify?.strings?.cart?.checkoutBlockedOos || 'Remove sold-out items before checkout');
      syncCartCheckoutState();
      return;
    }
    if (!form.closest('[data-cart-drawer]')) return;
    e.preventDefault();
    const root = window.Shopify?.routes?.root || '/';
    const btn = checkoutBtn;
    btn.setAttribute('aria-busy', 'true');
    (async () => {
      try {
        cancelDeferredCartSync();

        await Promise.all([...lineQtyChains.values()]);
        await cartMutationChain;

        let cart = window.GhaliaCart?.lastCart;
        if (!cart?.items?.length) {
          cart = await withTimeout(fetchCart({ force: true }), 800, null);
        }

        if (cart) {
          cart =
            (await withTimeout(prepareCartForCheckout(cart), CHECKOUT_PREP_TIMEOUT_MS, cart)) ||
            cart;
        }

        window.location.assign(`${root}checkout`);
      } catch (err) {
        console.error(err);
        btn.removeAttribute('aria-busy');
        syncCartCheckoutState();
        showGhaliaToast('Could not sync bag before checkout. Try again.');
      }
    })();
  });

  document.addEventListener('ghalia:cart:error', (e) => {
    const err = e.detail;
    const message = err?.message || (typeof err === 'string' ? err : '');
    if (message) showGhaliaToast(message);
  });

  /* ---------- Marquee duplication (for seamless loop) ---------- */
  $$('[data-marquee]').forEach((m) => {
    const track = m.querySelector('.marquee__track');
    if (!track) return;
    track.innerHTML = track.innerHTML + track.innerHTML;
  });

  /* ---------- Lazy reveal ---------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.1 });
    $$('[data-reveal]').forEach((el) => io.observe(el));
  }

  /* ---------- Footer columns — static desktop, accordion mobile ---------- */
  const footerColMq = window.matchMedia('(max-width: 1024px)');

  const syncFooterCols = () => {
    $$('[data-footer-col]').forEach((col) => {
      const head = col.querySelector('.site-footer__col-head');
      const isMobile = footerColMq.matches;
      if (!isMobile) {
        col.classList.remove('is-open');
        head?.setAttribute('aria-expanded', 'true');
        return;
      }
      const open = col.classList.contains('is-open');
      head?.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  $$('[data-footer-col] .site-footer__col-head').forEach((head) => {
    head.addEventListener('click', () => {
      if (!footerColMq.matches) return;
      const col = head.closest('[data-footer-col]');
      if (!col) return;
      const willOpen = !col.classList.contains('is-open');
      $$('[data-footer-col]').forEach((item) => item.classList.remove('is-open'));
      if (willOpen) col.classList.add('is-open');
      syncFooterCols();
    });
  });

  footerColMq.addEventListener('change', syncFooterCols);
  syncFooterCols();

  $$('details.pdp__accordion, details.pdp-accordion, details.faq-accordion, details.facet, details.ts-facet').forEach((d) => {
    d.setAttribute('aria-expanded', d.open ? 'true' : 'false');
    d.addEventListener('toggle', () => d.setAttribute('aria-expanded', d.open ? 'true' : 'false'));
  });

  /* ---------- Product card image swipe (touch + drag) ---------- */
  const boundMediaTracks = new WeakSet();
  const preloadedSiblingImages = new Set();

  const releaseMediaTrack = (track) => {
    if (!track) return;
    const state = track._mediaDrag;
    if (state?.pointerId != null) {
      try { track.releasePointerCapture(state.pointerId); } catch (_) { /* noop */ }
    }
    track._mediaDrag = { dragging: false, pointerId: null, moved: false };
    track.classList.remove('is-dragging');
  };

  const initProductMediaTrack = (track) => {
    if (!track || boundMediaTracks.has(track)) return;
    boundMediaTracks.add(track);
    track._mediaDrag = { dragging: false, pointerId: null, moved: false };

    const link = track.closest('.product-card__media-link');

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('.product-card__swatch, [data-sibling-swatch]')) return;
      e.stopPropagation();
      releaseMediaTrack(track);
      track._mediaDrag.dragging = true;
      track._mediaDrag.moved = false;
      track._mediaDrag.pointerId = e.pointerId;
      track._mediaDrag.startX = e.clientX;
      track._mediaDrag.scrollStart = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(e.pointerId);
    });

    track.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const state = track._mediaDrag;
      if (!state?.dragging) return;
      const dx = e.clientX - state.startX;
      if (Math.abs(dx) > 4) state.moved = true;
      track.scrollLeft = state.scrollStart - dx;
    });

    const endDrag = (e) => {
      if (e?.pointerType && e.pointerType !== 'mouse') return;
      const state = track._mediaDrag;
      if (!state?.dragging) return;
      if (e?.pointerId != null && track.hasPointerCapture?.(e.pointerId)) {
        try { track.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
      }
      state.dragging = false;
      state.pointerId = null;
      track.classList.remove('is-dragging');
      if (state.moved) setTimeout(() => { state.moved = false; }, 0);
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    if (link) {
      link.addEventListener('click', (e) => {
        if (track._mediaDrag?.moved) e.preventDefault();
      });
    }
  };

  const parseSiblingImages = (swatch) => {
    const raw = swatch.getAttribute('data-sibling-images') || '';
    if (raw) {
      return raw.split('||').filter(Boolean).map((part) => {
        const sep = part.indexOf('::');
        if (sep === -1) return { src: part, srcset: '' };
        return { src: part.slice(0, sep), srcset: part.slice(sep + 2) };
      });
    }
    const src = swatch.getAttribute('data-sibling-image') || '';
    const srcset = swatch.getAttribute('data-sibling-srcset') || '';
    return src ? [{ src, srcset }] : [];
  };

  const preloadSiblingImages = (swatch) => {
    parseSiblingImages(swatch).forEach(({ src }) => {
      if (!src || preloadedSiblingImages.has(src)) return;
      preloadedSiblingImages.add(src);
      const img = new Image();
      img.src = src;
    });
  };

  /* ---------- Product card icon tone (light photo → black icons, dark → white) ---------- */
  const iconToneCanvas = document.createElement('canvas');
  const iconToneCtx = iconToneCanvas.getContext('2d', { willReadFrequently: true });
  const ICON_TONE_LUMINANCE = 138;
  const iconTonePending = new WeakSet();
  const iconToneProbeCache = new Map();

  const getActiveMediaSlideIndex = (track) => {
    const slides = track.querySelectorAll('.product-card__media-slide');
    if (!slides.length) return 0;
    const w = track.clientWidth;
    if (!w) return 0;
    const scroll = Math.abs(track.scrollLeft);
    return Math.max(0, Math.min(slides.length - 1, Math.round(scroll / w)));
  };

  const initCartUpsellDrawer = (root = document) => {
    const drawer = root?.matches?.('[data-cart-drawer]')
      ? root
      : root.querySelector?.('[data-cart-drawer]') || document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    drawer.querySelectorAll('[data-cart-upsell-card] [data-product-media-track]').forEach((track) => {
      initProductMediaTrack(track);
      const card = track.closest('[data-cart-upsell-card]');
      const dotsWrap = card?.querySelector('[data-cart-upsell-dots]');
      if (!dotsWrap || track._upsellDotsBound) return;
      track._upsellDotsBound = true;
      const dots = dotsWrap.querySelectorAll('.cart-upsell-card__dot');
      const updateDots = () => {
        const idx = getActiveMediaSlideIndex(track);
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === idx));
      };
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(track._upsellDotTimer);
          track._upsellDotTimer = setTimeout(updateDots, 50);
        },
        { passive: true }
      );
      updateDots();
    });
  };

  const getCardMediaImage = (card) => {
    const track = card.querySelector('[data-product-media-track]');
    if (track?.clientWidth > 0) {
      const slides = track.querySelectorAll('.product-card__media-slide');
      const tracked = slides[getActiveMediaSlideIndex(track)]?.querySelector('.product-card__media-primary');
      if (tracked) return tracked;
    }
    return card.querySelector('.product-card__media-primary');
  };

  const luminanceFromPixel = (data, i) => {
    if (data[i + 3] < 40) return null;
    return 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  };

  const avgBandLuminance = (data, width, height, xStart, xEnd) => {
    let sum = 0;
    let count = 0;
    const x0 = Math.max(0, xStart);
    const x1 = Math.min(width, xEnd);
    for (let y = 0; y < height; y += 1) {
      for (let x = x0; x < x1; x += 2) {
        const lum = luminanceFromPixel(data, (y * width + x) * 4);
        if (lum == null) continue;
        sum += lum;
        count += 1;
      }
    }
    return count ? sum / count : null;
  };

  const luminanceToTone = (lum) => (lum == null || lum >= ICON_TONE_LUMINANCE ? 'light' : 'dark');

  /* Bottom band at inline-start + inline-end (cart + wishlist); min luminance → both icons same color */
  const measureIconRegionLuminance = (img) => {
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return null;
    const bandH = Math.min(Math.max(18, Math.floor(nh * 0.24)), nh);
    const srcY = nh - bandH;
    const sampleW = Math.min(96, nw);
    const sampleH = Math.min(36, bandH);
    iconToneCanvas.width = sampleW;
    iconToneCanvas.height = sampleH;
    try {
      iconToneCtx.drawImage(img, 0, srcY, nw, bandH, 0, 0, sampleW, sampleH);
      const { data } = iconToneCtx.getImageData(0, 0, sampleW, sampleH);
      const bandEdge = Math.ceil(sampleW * 0.36);
      const bandInner = Math.floor(sampleW * 0.64);
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      /* LTR: cart=start=left, wishlist=end=right. RTL: cart=start=right, wishlist=end=left */
      const startLum = rtl
        ? avgBandLuminance(data, sampleW, sampleH, bandInner, sampleW)
        : avgBandLuminance(data, sampleW, sampleH, 0, bandEdge);
      const endLum = rtl
        ? avgBandLuminance(data, sampleW, sampleH, 0, bandEdge)
        : avgBandLuminance(data, sampleW, sampleH, bandInner, sampleW);
      if (startLum == null && endLum == null) return null;
      if (startLum == null) return endLum;
      if (endLum == null) return startLum;
      return Math.min(startLum, endLum);
    } catch (_) {
      return null;
    }
  };

  const probeImageLuminance = (src) => {
    if (!src) return Promise.resolve(null);
    const dir = document.documentElement.getAttribute('dir') || 'ltr';
    const cacheKey = `region|${src}|${dir}`;
    if (iconToneProbeCache.has(cacheKey)) return iconToneProbeCache.get(cacheKey);
    const pending = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => resolve(measureIconRegionLuminance(probe));
      probe.onerror = () => resolve(null);
      probe.src = src;
    });
    iconToneProbeCache.set(cacheKey, pending);
    return pending;
  };

  const resolveIconLuminance = async (img) => {
    try {
      const direct = measureIconRegionLuminance(img);
      if (direct != null) return direct;
    } catch (_) {}
    const src = img.currentSrc || img.src;
    if (!src) return null;
    return probeImageLuminance(src);
  };

  const applyCardIconTone = (card, tone) => {
    if (!card?.querySelector('.product-card__icons')) return;
    card.dataset.mediaTone = tone === 'dark' ? 'dark' : 'light';
    card.querySelectorAll('.product-card__icon-btn').forEach((btn) => {
      delete btn.dataset.mediaTone;
    });
  };

  const updateCardIconTone = (card) => {
    if (!card?.querySelector('.product-card__icons')) return;
    const img = getCardMediaImage(card);
    if (!img) {
      applyCardIconTone(card, 'light');
      return;
    }
    const commit = async () => {
      const lum = await resolveIconLuminance(img);
      applyCardIconTone(card, luminanceToTone(lum));
    };
    if (img.complete && img.naturalWidth) {
      commit();
      return;
    }
    img.addEventListener('load', () => commit(), { once: true });
    img.addEventListener('error', () => applyCardIconTone(card, 'light'), { once: true });
  };

  const scheduleCardIconTone = (card) => {
    if (!card || iconTonePending.has(card)) return;
    iconTonePending.add(card);
    requestAnimationFrame(() => {
      iconTonePending.delete(card);
      updateCardIconTone(card);
    });
  };

  const bindCardIconTone = (card) => {
    if (!card?.querySelector('.product-card__icons') || card._iconToneBound) return;
    card._iconToneBound = true;
    const track = card.querySelector('[data-product-media-track]');
    if (track) {
      let scrollTimer;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => scheduleCardIconTone(card), 80);
      }, { passive: true });
    }
    card.querySelectorAll('.product-card__media-primary').forEach((img) => {
      img.addEventListener('load', () => scheduleCardIconTone(card), { passive: true });
    });
  };

  const refreshAllCardIconTones = () => {
    iconToneProbeCache.clear();
    $$('[data-product-card]').forEach((card) => {
      card._iconToneBound = false;
      bindCardIconTone(card);
      scheduleCardIconTone(card);
    });
  };

  const initProductCardIconTones = (root = document) => {
    $$('[data-product-card]', root).forEach((card) => {
      bindCardIconTone(card);
      scheduleCardIconTone(card);
    });
  };

  if (document.documentElement) {
    new MutationObserver(() => refreshAllCardIconTones()).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir', 'lang'],
    });
  }

  const getCardImagesFromDom = (card) => Array.from(card.querySelectorAll('.product-card__media-primary'))
    .map((img) => ({ src: img.currentSrc || img.src, srcset: img.srcset || '' }))
    .filter((item) => item.src);

  const cacheCardMedia = (card) => {
    const images = getCardImagesFromDom(card);
    if (images.length) card.dataset.cardMediaCache = JSON.stringify(images);
  };

  const getCardMediaCache = (card) => {
    try {
      return JSON.parse(card.dataset.cardMediaCache || '[]');
    } catch (_) {
      return [];
    }
  };

  const setCardMedia = (card, images, title) => {
    const mediaWrap = card.querySelector('.product-card__media');
    if (!mediaWrap) return;

    let valid = images.filter((item) => item?.src && item.src.trim());
    if (!valid.length) {
      valid = getCardMediaCache(card);
      if (!valid.length) return;
    }

    const previousMarkup = mediaWrap.innerHTML;
    const cached = getCardMediaCache(card);
    const restoreOnError = () => {
      if (cached.length) {
        setCardMedia(card, cached, title);
        return;
      }
      if (previousMarkup) mediaWrap.innerHTML = previousMarkup;
    };

    let track = mediaWrap.querySelector('[data-product-media-track]');
    if (track) releaseMediaTrack(track);

    const buildImg = ({ src, srcset }, index) => {
      const img = document.createElement('img');
      img.className = 'product-card__media-primary';
      img.src = src;
      if (srcset) img.srcset = srcset;
      img.sizes = '(min-width: 769px) 329px, (min-width: 391px) 224px, 177px';
      img.width = 329;
      img.height = 362;
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.decoding = index === 0 ? 'sync' : 'async';
      if (index === 0) img.fetchPriority = 'high';
      if (title) img.alt = title;
      img.addEventListener('error', restoreOnError, { once: true });
      return img;
    };

    mediaWrap.innerHTML = '';

    if (valid.length === 1) {
      mediaWrap.classList.remove('product-card__media--swipeable');
      mediaWrap.appendChild(buildImg(valid[0], 0));
    } else {
      track = document.createElement('div');
      track.className = 'product-card__media-track';
      track.dataset.productMediaTrack = '';
      const frag = document.createDocumentFragment();
      valid.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'product-card__media-slide';
        slide.appendChild(buildImg(item, index));
        frag.appendChild(slide);
      });
      track.appendChild(frag);
      mediaWrap.appendChild(track);
      track.scrollLeft = 0;
      mediaWrap.classList.add('product-card__media--swipeable');
      initProductMediaTrack(track);
    }

    card.dataset.cardMediaCache = JSON.stringify(valid);
    card._iconToneBound = false;
    bindCardIconTone(card);
    scheduleCardIconTone(card);
  };

  $$('[data-product-media-track]').forEach(initProductMediaTrack);
  initCartUpsellDrawer();
  initProductCardIconTones();

  /* ---------- Product cards — cache media + decorative swatches ---------- */
  $$('[data-product-card]').forEach((card) => {
    cacheCardMedia(card);

    const decorativeSwatches = card.querySelectorAll('.product-card__swatch:not([data-sibling-swatch])');
    if (!decorativeSwatches.length) return;

    decorativeSwatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        decorativeSwatches.forEach((el) => {
          const active = el === swatch;
          el.setAttribute('aria-pressed', active ? 'true' : 'false');
          if (active) el.setAttribute('aria-current', 'true');
          else el.removeAttribute('aria-current');
        });
      });
    });
  });

  /* ---------- Product sibling swatches (gold / silver SKU swap) ---------- */
  $$('[data-product-card]').forEach((card) => {
    const swatches = card.querySelectorAll('[data-sibling-swatch]');
    if (!swatches.length) return;

    cacheCardMedia(card);

    const mediaLink = card.querySelector('.product-card__media-link');
    const titleLink = card.querySelector('[data-product-title]');
    const materialEl = card.querySelector('[data-product-material]');
    const priceEl = card.querySelector('[data-product-price]');
    const quickAddBtns = card.querySelectorAll('[data-quick-add]');
    const wishlistBtns = card.querySelectorAll('[data-wishlist-toggle]');

    const applySibling = (swatch) => {
      const siblingUrl = swatch.getAttribute('data-sibling-url') || '';
      const siblingTitle = swatch.getAttribute('data-sibling-title') || '';
      const siblingMaterial = swatch.getAttribute('data-sibling-material') || '';
      const siblingPrice = swatch.getAttribute('data-sibling-price') || '';
      const siblingVariantId = swatch.getAttribute('data-sibling-variant-id') || '';
      const siblingId = swatch.getAttribute('data-sibling-id') || '';

      if (titleLink && siblingTitle) {
        if (siblingUrl) {
          titleLink.href = siblingUrl;
          titleLink.dataset.productUrl = siblingUrl;
        }
        titleLink.textContent = siblingTitle;
      }
      if (materialEl) {
        materialEl.textContent = siblingMaterial || '';
        materialEl.hidden = !siblingMaterial;
      }
      if (priceEl && siblingPrice) {
        const priceText = siblingPrice.replace(/<[^>]*>/g, '').trim();
        if (priceText && !/^0(\.0+)?\s/.test(priceText) && priceText !== '0.00') {
          priceEl.innerHTML = siblingPrice;
        }
      }
      if (mediaLink && siblingUrl) {
        mediaLink.href = siblingUrl;
        mediaLink.dataset.productUrl = siblingUrl;
        if (siblingTitle) mediaLink.setAttribute('aria-label', siblingTitle);
      }

      const siblingImages = parseSiblingImages(swatch).filter((item) => item?.src && item.src.trim());
      const siblingImageOnly = (swatch.getAttribute('data-sibling-image') || '').trim();
      if (siblingImages.length) {
        setCardMedia(card, siblingImages, siblingTitle);
      } else if (siblingImageOnly) {
        setCardMedia(card, [{
          src: siblingImageOnly,
          srcset: swatch.getAttribute('data-sibling-srcset') || '',
        }], siblingTitle);
      }

      quickAddBtns.forEach((btn) => {
        if (siblingVariantId) btn.dataset.quickAdd = siblingVariantId;
      });
      wishlistBtns.forEach((btn) => {
        if (siblingId) btn.dataset.wishlistToggle = siblingId;
        if (siblingVariantId) btn.dataset.variantId = siblingVariantId;
        if (siblingUrl) btn.dataset.productUrl = siblingUrl;
        setWishlistBtnState(btn, readLocalWishlist().some((item) => wishlistItemKey(item.productId, item.variantId) === wishlistItemKey(siblingId, siblingVariantId)));
      });
      if (siblingId) card.dataset.productId = siblingId;

      swatches.forEach((el) => {
        const active = el === swatch;
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    };

    swatches.forEach((swatch) => {
      swatch.addEventListener('pointerenter', () => preloadSiblingImages(swatch), { passive: true });
      swatch.addEventListener('focus', () => preloadSiblingImages(swatch));
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        releaseMediaTrack(card.querySelector('[data-product-media-track]'));
        applySibling(swatch);
      });
    });
  });

  /* ---------- Featured row drag scroll (desktop mouse swipe) ---------- */
  $$('[data-scroll-track]').forEach((row) => {
    let dragging = false;
    let moved = false;
    let startX = 0;
    let scrollStart = 0;

    const isInteractiveTarget = (target) => target.closest(
      'button, [data-quick-add], [data-wishlist-toggle], [data-sibling-swatch], .product-card__swatch, a[href], .irl-card__dock-link, [data-irl-video-trigger], [data-irl-dock-expand]'
    );

    row.addEventListener('pointerdown', (e) => {
      if (isInteractiveTarget(e.target)) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      scrollStart = row.scrollLeft;
      row.setPointerCapture(e.pointerId);
    });
    row.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      row.scrollLeft = scrollStart - dx;
    });
    const end = () => { dragging = false; };
    row.addEventListener('pointerup', end);
    row.addEventListener('pointercancel', end);
    row.addEventListener('click', (e) => {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }, true);
  });

  /* ---------- IRL — scroll card to center on video tap ---------- */
  const scrollIrlCardToCenter = (track, card) => {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const target = track.scrollLeft + (cardRect.left - trackRect.left) - (trackRect.width - cardRect.width) / 2;
    track.scrollTo({ left: target, behavior: 'smooth' });
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-irl-video-trigger]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    const card = trigger.closest('.irl-card, .pdp-irl-card');
    const track = trigger.closest('[data-irl-track]');
    if (!card || !track) return;
    scrollIrlCardToCenter(track, card);
  });

  document.addEventListener('click', (e) => {
    const muteBtn = e.target.closest('[data-pdp-irl-mute], [data-pdp-irl-modal-mute]');
    if (!muteBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const media = muteBtn.closest('.pdp-irl-card__media, .pdp-irl-modal__video-wrap');
    const video = media?.querySelector('.pdp-irl-card__video, .pdp-irl-modal__video, .irl-card__video');
    if (!video || video.tagName !== 'VIDEO') return;
    video.muted = !video.muted;
    muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
    if (!video.muted) video.play().catch(() => {});
  });

  const closeIrlDocks = (except) => {
    $$('.irl-card__dock.is-expanded').forEach((dock) => {
      if (dock === except) return;
      dock.classList.remove('is-expanded');
      const btn = dock.querySelector('[data-irl-dock-expand]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (e) => {
    const expandBtn = e.target.closest('[data-irl-dock-expand]');
    if (expandBtn) {
      e.preventDefault();
      e.stopPropagation();
      const dock = expandBtn.closest('.irl-card__dock');
      if (!dock) return;
      const isExpanded = dock.classList.contains('is-expanded');
      closeIrlDocks();
      if (!isExpanded) {
        dock.classList.add('is-expanded');
        expandBtn.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    closeIrlDocks();
  });

  /* Keep homepage IRL videos muted autoplay (PDP videos can be unmuted via mute button) */
  $$('.irl-card .irl-card__video').forEach((video) => {
    video.muted = true;
    video.setAttribute('muted', '');
    const play = () => video.play().catch(() => {});
    play();
    video.addEventListener('volumechange', () => {
      if (!video.muted) {
        video.muted = true;
      }
    });
  });

  $$('.pdp-irl-card__video').forEach((video) => {
    if (video.tagName !== 'VIDEO') return;
    video.muted = true;
    video.setAttribute('muted', '');
    video.play().catch(() => {});
  });

  /* ---------- Announcement bar promos ---------- */

  if (primaryBar) {
    const closeBtn = primaryBar.querySelector('[data-announcement-close]');
    closeBtn?.addEventListener('click', () => {
      primaryBar.classList.add('is-hidden');
      updateStickyNav();
    });
  }

  document.querySelectorAll('[data-announcement-promos]').forEach((wrap) => {
    const promos = [...wrap.querySelectorAll('[data-announcement-promo]')];
    if (promos.length < 2) return;
    const section = wrap.closest('[data-announcement-stack]');
    const rotateSec = Number(section?.dataset.rotateSeconds) || 5;
    let index = 0;
    setInterval(() => {
      promos[index]?.classList.remove('is-active');
      index = (index + 1) % promos.length;
      promos[index]?.classList.add('is-active');
    }, Math.max(3, rotateSec) * 1000);
  });

  updateStickyNav();
  window.addEventListener('resize', updateStickyNav, { passive: true });

  /* ---------- Mobile locale (popover or sheet) ---------- */
  const localeSheet = document.querySelector('[data-locale-sheet]');
  const localePopover = document.querySelector('[data-locale-popover]');
  const localePanel = localePopover?.querySelector('[data-locale-panel]');
  const localeTriggers = $$('[data-locale-toggle]');

  const setLocaleOpen = (open) => {
    localeTriggers.forEach((btn) => btn.setAttribute('aria-expanded', open ? 'true' : 'false'));
    if (localePopover) {
      localePopover.dataset.open = open ? 'true' : 'false';
      localePanel?.setAttribute('aria-hidden', open ? 'false' : 'true');
      return;
    }
    if (!localeSheet) return;
    localeSheet.dataset.open = open ? 'true' : 'false';
    localeSheet.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (overlay) overlay.dataset.open = open ? 'true' : 'false';
    document.documentElement.classList.toggle('locale-sheet-open', open);
    document.documentElement.style.overflow = open ? 'hidden' : '';
    document.body.style.overflow = open ? 'hidden' : '';
  };

  localeTriggers.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const root = localePopover || localeSheet;
      if (!root) return;
      const open = root.dataset.open !== 'true';
      setLocaleOpen(open);
    });
  });

  localeSheet?.querySelector('[data-locale-close]')?.addEventListener('click', () => setLocaleOpen(false));
  localeSheet?.addEventListener('click', (e) => {
    if (e.target === localeSheet) setLocaleOpen(false);
  });
  document.addEventListener('click', (e) => {
    if (localePopover?.dataset.open === 'true' && !e.target.closest('[data-locale-popover]')) {
      setLocaleOpen(false);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (localePopover?.dataset.open === 'true' || localeSheet?.dataset.open === 'true') setLocaleOpen(false);
  });

  /* ---------- Testimonials carousel (mobile / tablet) ---------- */
  const testimonialsMq = window.matchMedia('(max-width: 1023px)');
  $$('[data-testimonials-carousel]').forEach((track) => {
    const board = track.closest('.testimonials__board');
    if (!board) return;
    const prev = board.querySelector('[data-testimonials-prev]');
    const next = board.querySelector('[data-testimonials-next]');
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';

    const scrollBySlide = (dir) => {
      const step = track.clientWidth || 1;
      const delta = dir * step * (rtl ? -1 : 1);
      track.scrollBy({ left: delta, behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollBySlide(-1));
    next?.addEventListener('click', () => scrollBySlide(1));
    track.addEventListener('keydown', (e) => {
      if (!testimonialsMq.matches) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollBySlide(rtl ? 1 : -1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollBySlide(rtl ? -1 : 1);
      }
    });
  });

  hydrateCartWrapAddForm();
  bindPreviewThemePersistence();

  const initHeroVideos = () => {
    const playHeroVideo = (wrap, video) => {
      if (!video || document.hidden) return;
      const rateRaw = parseFloat(wrap.dataset.playbackRate || '1.2');
      const rate = Number.isFinite(rateRaw) ? Math.min(1.5, Math.max(1, rateRaw)) : 1.2;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.playbackRate = rate;
      video.defaultPlaybackRate = rate;
      const attempt = () => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      };
      if (video.readyState >= 2) attempt();
      else video.addEventListener('loadeddata', attempt, { once: true });
    };

    $$('[data-hero-video]').forEach((wrap) => {
      const video = wrap.querySelector('.hero__video');
      playHeroVideo(wrap, video);
    });

    document.addEventListener('visibilitychange', () => {
      $$('[data-hero-video] .hero__video').forEach((video) => {
        if (document.hidden) video.pause();
        else video.play()?.catch(() => {});
      });
    });
  };

  initHeroVideos();
})();
